import { Plus, Save, Trash2, ChevronDown, ChevronUp, Edit, X, GripVertical, Info } from "lucide-react";
import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSearchParams } from "react-router";
import {
  formatInvoiceProfileConfigurationTitle,
  readInvoiceProfileClientFromSession,
  writeInvoiceProfileClientToSession,
} from "../utils/pageTitles";
import { formatContractRateDisplay } from "../utils/formatContractRateDisplay";
import { sortSavedProfileServicesNewestFirst } from "../utils/sortInvProfileServices";
import {
  formatApiDateForDisplay,
  ServiceEndDateCell,
  serviceHasEndDate,
  todayIsoDate,
} from "../components/ServiceEndDateCell";
import { BillingClassificationRowEditor } from "../components/BillingClassificationRowEditor";
import { LaborMappingRowEditor } from "../components/LaborMappingRowEditor";
import {
  BILLING_DATA_CLASSIFICATION_PRODUCT_DIVISION,
  BILLING_DATA_CLASSIFICATION_WAREHOUSE,
  createEmptyBillingClassificationRow,
  billingClassificationRowsFromApi,
  ensureBillingClassificationRows,
  syncLegacyBillingFieldsFromRows,
  buildBillingClassificationsSaveBody,
  validateBillingClassificationRows,
  validateSingleBillingClassificationRowErrors,
  isBillingClassificationRowComplete,
  resolveRowBillingDataClassificationAttributeValue,
  serviceHasBillingClassificationRows,
} from "../utils/invProfileBillingClassification";
import {
  createEmptyLaborMappingRow,
  laborMappingRowsFromOpCodes,
  ensureLaborMappingRows,
  buildOpCodesFromLaborMappingRows,
  validateLaborMappingRows,
  validateSingleLaborMappingRowErrors,
  isLaborMappingRowComplete,
  serviceHasLaborMappingRows,
  defaultLaborMappingCompanyNumber,
} from "../utils/invProfileLaborMapping";
import {
  useCreateClientConfigurationInvProfileMutation,
  useCreateClientConfigurationInvProfileDetailMutation,
  useUpdateClientConfigurationInvProfileDetailMutation,
  useEndClientConfigurationInvProfileDetailMutation,
  useDeactivateClientConfigurationInvProfileMutation,
  useGetClientConfigurationEmployeesQuery,
  useGetClientConfigurationCustomerCompanyNumbersQuery,
  selectAuth,
  useAppSelector,
  useGetClientConfigurationContractServiceInvoiceDescriptionOptionsQuery,
  useGetClientConfigurationOperationsQuery,
  useGetClientConfigurationCustomerInvProfileDetailsQuery,
  useGetClientConfigurationCustomerInvProfilesQuery,
  useGetClientConfigurationCustomerPoOptionsQuery,
  useGetClientConfigurationCustomerJacketJobsQuery,
  useGetClientConfigurationCustomerInvProfileJobnosQuery,
} from "../store";

function normalizeProfileId(id) {
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function profileIdsMatch(a, b) {
  const left = normalizeProfileId(a);
  const right = normalizeProfileId(b);
  return left != null && left === right;
}

function serviceIdsMatch(a, b) {
  return String(a ?? "") === String(b ?? "");
}

/** True when the profile has a service row not yet persisted (Add Service in progress). */
function profileHasUnsavedService(profile) {
  return (profile?.services ?? []).some((s) => !s.invProfileDetailId);
}

/** Services persisted to {@code act_inv_profile_detail} (excludes Add Service draft rows). */
function savedProfileServices(services) {
  return (services ?? []).filter((s) => s.invProfileDetailId);
}

function countSavedProfileServices(services) {
  return savedProfileServices(services).length;
}

/** Next sequence_display for a new invoice profile detail row (1-based). */
function nextProfileServiceSequenceDisplay(services) {
  const saved = savedProfileServices(services);
  const maxSeq = saved.reduce((max, service) => {
    const n = Number(service.sequenceDisplay);
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);
  return maxSeq + 1;
}

function normalizedSequenceDisplayValue(sequenceDisplay) {
  const n = Number(sequenceDisplay);
  return Number.isFinite(n) ? n : null;
}

function formatProfileServiceApiError(data, fallback) {
  if (data?.errors && typeof data.errors === "object") {
    const parts = Object.entries(data.errors).map(
      ([key, value]) => `${key}: ${typeof value === "string" ? value : String(value)}`
    );
    if (parts.length > 0) {
      return parts.join(" ");
    }
  }
  return data?.detail || data?.title || fallback;
}


const PROFILE_SERVICE_ROW_DND_TYPE = "PROFILE_SERVICE_ROW";

function DraggableProfileServiceGroup({
  profileId,
  serviceId,
  index,
  moveService,
  onDragStart,
  onDragEnd,
  dragDisabled = false,
  children,
}) {
  const tbodyRef = useRef(null);
  const handleRef = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: PROFILE_SERVICE_ROW_DND_TYPE,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item, monitor) {
      if (!tbodyRef.current || item.profileId !== profileId || dragDisabled) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = tbodyRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveService(profileId, dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: PROFILE_SERVICE_ROW_DND_TYPE,
    canDrag: () => !dragDisabled,
    item: () => {
      onDragStart?.(profileId);
      return { profileId, serviceId, index };
    },
    end: (item, monitor) => {
      if (item?.profileId != null) {
        onDragEnd?.(item.profileId, monitor.didDrop());
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(handleRef);
  drop(tbodyRef);

  return (
    <tbody
      ref={tbodyRef}
      data-handler-id={handlerId}
      className="divide-y divide-gray-200 dark:divide-gray-800"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {children(handleRef, dragDisabled)}
    </tbody>
  );
}

function formatProfilePoNumberDisplay(invPoNum) {
  const v = String(invPoNum ?? "").trim();
  return v || "N/A";
}

/** {@code act_inv_profile.active_flag}: null/undefined is treated as active (legacy rows). */
function isInvoiceProfileActive(activeFlag) {
  return activeFlag !== false && activeFlag !== "false" && activeFlag !== 0 && activeFlag !== "0";
}

const BILLING_PROFILE_STATUS_FILTER_ALL = "All";
const BILLING_PROFILE_STATUS_FILTER_ACTIVE = "Active";
const BILLING_PROFILE_STATUS_FILTER_DEACTIVE = "DeActive";
const BILLING_PROFILE_STATUS_FILTER_OPTIONS = [
  BILLING_PROFILE_STATUS_FILTER_ALL,
  BILLING_PROFILE_STATUS_FILTER_ACTIVE,
  BILLING_PROFILE_STATUS_FILTER_DEACTIVE,
];

const BILLING_PROFILE_COMPANY_FILTER_ALL = "All";

function billingProfileActiveFilterDescription(statusFilter, companyFilter) {
  const parts = [];
  if (statusFilter !== BILLING_PROFILE_STATUS_FILTER_ALL) {
    parts.push(statusFilter);
  }
  if (companyFilter !== BILLING_PROFILE_COMPANY_FILTER_ALL) {
    parts.push(`Company # ${companyFilter}`);
  }
  return parts.join(" / ");
}

function mapInvProfileRowToUi(row) {
  const profileId = normalizeProfileId(row.invProfileId ?? row.inv_profile_id);
  const activeFlag = row.activeFlag ?? row.active_flag;
  const companyNumbers = Array.isArray(row.companyNumbers)
    ? row.companyNumbers.map((n) => String(n ?? "").trim()).filter(Boolean)
    : String(row.companyNumber ?? row.company_number ?? "")
        .trim()
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean);
  const companyNumber =
    companyNumbers.length > 0
      ? companyNumbers.join(", ")
      : String(row.companyNumber ?? row.company_number ?? "").trim();
  return {
    id: profileId ?? row.invProfileId ?? row.inv_profile_id,
    clientCode: String(row.clientCode ?? "").trim(),
    companyNumbers,
    companyNumber,
    description: "",
    invoiceNameProgram: String(row.invoiceNameProgram ?? row.invoice_program_name ?? "").trim(),
    invPoNum: String(row.invPoNum ?? row.inv_ponum ?? "").trim(),
    activeFlag: isInvoiceProfileActive(activeFlag),
    services: [],
    isExpanded: false,
  };
}

/** Maps stored {@code attribute_value} into the drawer field for the active classification type. */
function hydrateBillingClassificationFieldsForUi(row) {
  const classification = String(
    row.billingDataClassification ?? row.billing_data_classification ?? ""
  ).trim();
  const value = String(
    row.billingDataClassificationValue ?? row.billing_data_classification_value ?? ""
  ).trim();
  const productClass = String(row.productClass ?? row.product_class ?? "").trim();
  const productCode = String(row.productCode ?? row.product_code ?? "").trim();
  const productType = String(row.productType ?? row.product_type ?? "").trim();
  const productLine = String(row.productLine ?? row.product_line ?? "").trim();
  const warehouseName = String(row.warehouseName ?? row.warehouse_name ?? "").trim();
  if (!value) {
    return { productClass, productCode, productType, productLine, warehouseName };
  }
  switch (classification) {
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS:
      return {
        productClass: productClass || value,
        productCode,
        productType,
        productLine,
        warehouseName,
      };
    case BILLING_DATA_CLASSIFICATION_PRODUCT_CODE:
      return {
        productClass,
        productCode: productCode || value,
        productType,
        productLine,
        warehouseName,
      };
    case BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE:
      return {
        productClass,
        productCode,
        productType: productType || value,
        productLine,
        warehouseName,
      };
    case BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE:
      return {
        productClass,
        productCode,
        productType,
        productLine: productLine || value,
        warehouseName,
      };
    case BILLING_DATA_CLASSIFICATION_WAREHOUSE:
      return {
        productClass,
        productCode,
        productType,
        productLine,
        warehouseName: warehouseName || value,
      };
    default:
      return { productClass, productCode, productType, productLine, warehouseName };
  }
}

function mapInvProfileDetailRowToUi(row, assignedCompanyNumbers = []) {
  const detailId = row.invProfileDetailId ?? row.inv_profile_detail_id;
  const endDate = formatApiDateForDisplay(row.endDate ?? row.end_date);
  const billingFields = hydrateBillingClassificationFieldsForUi(row);
  const billingClassificationRows = billingClassificationRowsFromApi(row, assignedCompanyNumbers);
  const laborMappingRows = laborMappingRowsFromOpCodes(
    row.opCodes ?? row.op_codes,
    assignedCompanyNumbers
  );
  return {
    id: detailId,
    invProfileDetailId: detailId,
    contractServiceId: String(row.contractServiceId ?? row.contract_service_id ?? ""),
    servicesRendered: String(row.servicesRendered ?? row.services_rendered ?? "").trim(),
    invoiceDescription: String(
      row.defaultInvoiceDescription ?? row.default_invoice_description ?? ""
    ).trim(),
    sequenceDisplay: row.sequenceDisplay ?? row.sequence_display ?? null,
    billingClassificationRows,
    billingDataClassification: String(
      row.billingDataClassification ?? row.billing_data_classification ?? ""
    ).trim(),
    billingDataClassificationValue: String(
      row.billingDataClassificationValue ?? row.billing_data_classification_value ?? ""
    ).trim(),
    productClass: billingFields.productClass,
    productCode: billingFields.productCode,
    productType: billingFields.productType,
    productLine: billingFields.productLine,
    warehouseName: billingFields.warehouseName,
    basedOn: String(row.basedOn ?? row.based_on ?? "").trim(),
    clientRate: formatContractRateDisplay(
      row.clientRate ?? row.client_rate ?? row.contractRate ?? row.contract_rate
    ),
    laborPercent: formatLaborPercentDisplay(row.laborPercent ?? row.labor_percent),
    frequency: "Monthly",
    effectiveDate: formatApiDateForDisplay(row.effectiveDate ?? row.effective_date),
    endDate,
    endAppliedByDisplayName: String(
      row.endAppliedByDisplayName ?? row.end_applied_by_display_name ?? ""
    ).trim(),
    isEnded: Boolean(endDate),
    opCodes: normalizeOpCodesFromApi(row.opCodes ?? row.op_codes),
    laborMappingRows,
  };
}

function normalizeOpCodesFromApi(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set();
  const list = [];
  for (const raw of value) {
    let entry;
    if (typeof raw === "string") {
      entry = { opCode: String(raw ?? "").trim(), jobNo: "", companyNumber: "" };
    } else if (raw && typeof raw === "object") {
      entry = {
        opCode: String(raw.opCode ?? raw.op_code ?? "").trim(),
        jobNo: String(raw.jobNo ?? raw.job_no ?? "").trim(),
        companyNumber: String(raw.companyNumber ?? raw.company_number ?? "").trim(),
      };
    } else {
      continue;
    }
    if (!entry.opCode) {
      continue;
    }
    const key = `${entry.opCode}\0${entry.jobNo}\0${entry.companyNumber}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    list.push(entry);
  }
  return list.sort((a, b) => {
    const byCode = a.opCode.localeCompare(b.opCode, undefined, { sensitivity: "base" });
    if (byCode !== 0) {
      return byCode;
    }
    const byJob = a.jobNo.localeCompare(b.jobNo, undefined, { sensitivity: "base" });
    if (byJob !== 0) {
      return byJob;
    }
    return a.companyNumber.localeCompare(b.companyNumber, undefined, { sensitivity: "base" });
  });
}

/** Selected op code strings for multiselect controls (one entry per op code). */
function selectedOpCodeStrings(entries) {
  const seen = new Set();
  const list = [];
  for (const entry of normalizeOpCodesFromApi(entries)) {
    if (!entry.opCode || seen.has(entry.opCode)) {
      continue;
    }
    seen.add(entry.opCode);
    list.push(entry.opCode);
  }
  return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** Merge multiselect op code strings with existing job/company values on a service. */
function mergeSelectedOpCodes(existingEntries, selectedCodes) {
  const existingByCode = new Map();
  for (const entry of normalizeOpCodesFromApi(existingEntries)) {
    if (!existingByCode.has(entry.opCode)) {
      existingByCode.set(entry.opCode, entry);
    }
  }
  const seen = new Set();
  const result = [];
  for (const raw of selectedCodes ?? []) {
    const code =
      typeof raw === "string"
        ? String(raw ?? "").trim()
        : String(raw?.opCode ?? raw?.op_code ?? "").trim();
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);
    const prior = existingByCode.get(code);
    result.push({
      opCode: code,
      jobNo: prior?.jobNo ?? "",
      companyNumber: prior?.companyNumber ?? "",
    });
  }
  return normalizeOpCodesFromApi(result);
}

/** Selected op codes on a profile service (supports legacy single {@code opCode} string). */
function normalizeServiceOpCodes(service) {
  if (Array.isArray(service?.opCodes)) {
    return normalizeOpCodesFromApi(service.opCodes);
  }
  const single = String(service?.opCode ?? "").trim();
  return single ? [{ opCode: single, jobNo: "", companyNumber: "" }] : [];
}

/** Send decimal string to API (strips currency formatting). */
function contractRateForApi(displayRate) {
  return String(displayRate ?? "").replace(/[$,]/g, "").trim();
}

function formatLaborPercentDisplay(value) {
  if (value == null || value === "") {
    return "";
  }
  const numeric = Number(String(value).replace(/%/g, "").replace(/,/g, "").trim());
  if (!Number.isFinite(numeric)) {
    return String(value ?? "").trim();
  }
  return String(numeric);
}

function laborPercentForApi(displayPercent) {
  return String(displayPercent ?? "").replace(/%/g, "").replace(/,/g, "").trim();
}

function validateLaborPercentValue(raw) {
  const normalized = laborPercentForApi(raw);
  if (!normalized) {
    return "Percentage is required.";
  }
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    if (Number.isFinite(Number(normalized))) {
      return "Percentage can have up to two decimal places.";
    }
    return "Percentage must be a number.";
  }
  const numeric = Number(normalized);
  if (!(numeric > 0) || numeric > 100) {
    return "Percentage must be greater than 0 and cannot be greater than 100.";
  }
  return "";
}

function hasContractRateValue(displayRate) {
  const normalized = contractRateForApi(displayRate);
  if (!normalized) {
    return false;
  }
  return Number.isFinite(Number(normalized));
}

const INVOICE_DESCRIPTION_VISIBLE_CHARS = 25;
const INVOICE_DESCRIPTION_MAX_CHARS = 250;

const BILLING_DATA_CLASSIFICATION_PRODUCT_CLASS = "Product Class";
const BILLING_DATA_CLASSIFICATION_PRODUCT_CODE = "Product Code";
const BILLING_DATA_CLASSIFICATION_PRODUCT_TYPE = "Product Type";
const BILLING_DATA_CLASSIFICATION_PRODUCTION_LINE = "Production Line";

function formatInvoiceDescriptionForDisplay(value) {
  const text = String(value ?? "").trim();
  if (!text) {
    return { text: "\u2014", fullText: null };
  }
  if (text.length <= INVOICE_DESCRIPTION_VISIBLE_CHARS) {
    return { text, fullText: null };
  }
  return {
    text: `${text.slice(0, INVOICE_DESCRIPTION_VISIBLE_CHARS)}\u2026`,
    fullText: text,
  };
}

/** Past-ended contract services (end date before today) are excluded from the Services Rendered dropdown. */
function isContractServiceActiveForServicesRenderedDropdown(opt, today = todayIsoDate()) {
  const end = String(opt?.endDate ?? opt?.end_date ?? "").trim();
  if (!end) {
    return true;
  }
  return end >= today;
}

function getContractServiceStartDate(contractServiceId, options) {
  const id = String(contractServiceId ?? "").trim();
  if (!id) {
    return "";
  }
  const opt = options.find((o) => String(o.contractServiceId) === id);
  return String(opt?.startDate ?? "").trim();
}

function validateEffectiveDateAgainstContractServiceStart(effectiveDate, serviceStartDate) {
  const effective = String(effectiveDate ?? "").trim();
  const start = String(serviceStartDate ?? "").trim();
  if (!effective || !start) {
    return null;
  }
  if (effective < start) {
    return `Effective date cannot be before the contract service start date (${start}).`;
  }
  return null;
}

/** Match a contract service for save / effective-date validation from services rendered (and description when ambiguous). */
function resolveContractServiceIdForService(service, options) {
  const servicesRendered = String(service?.servicesRendered ?? "").trim();
  if (!servicesRendered) {
    return "";
  }
  const matches = options.filter(
    (opt) => String(opt.servicesRendered ?? "").trim() === servicesRendered
  );
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return String(matches[0].contractServiceId ?? "");
  }
  const invoiceDesc = String(service?.invoiceDescription ?? "").trim().toLowerCase();
  if (!invoiceDesc) {
    return "";
  }
  const descMatches = matches.filter(
    (opt) =>
      String(opt.defaultInvoiceDescription ?? "").trim().toLowerCase() === invoiceDesc
  );
  if (descMatches.length === 1) {
    return String(descMatches[0].contractServiceId ?? "");
  }
  return "";
}

/** {@code act_contract_service.based_on} for a services-rendered dropdown value. */
function getBasedOnForServicesRendered(servicesRendered, options) {
  const sr = String(servicesRendered ?? "").trim();
  if (!sr) {
    return "";
  }
  const matches = options.filter(
    (opt) => String(opt.servicesRendered ?? "").trim() === sr
  );
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return String(matches[0].basedOn ?? "").trim();
  }
  const uniqueBasedOn = [
    ...new Set(matches.map((opt) => String(opt.basedOn ?? "").trim())),
  ];
  if (uniqueBasedOn.length === 1) {
    return uniqueBasedOn[0];
  }
  return String(matches[0].basedOn ?? "").trim();
}

/** {@code act_contract_service.default_invoice_description} for a services-rendered value. */
function getInvoiceDescriptionForServicesRendered(servicesRendered, options, currentInvoiceDescription) {
  const sr = String(servicesRendered ?? "").trim();
  if (!sr) {
    return "";
  }
  const matches = options.filter(
    (opt) => String(opt.servicesRendered ?? "").trim() === sr
  );
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return String(matches[0].defaultInvoiceDescription ?? "").trim();
  }
  const current = String(currentInvoiceDescription ?? "").trim().toLowerCase();
  if (current) {
    const descMatches = matches.filter(
      (opt) =>
        String(opt.defaultInvoiceDescription ?? "").trim().toLowerCase() === current
    );
    if (descMatches.length === 1) {
      return String(descMatches[0].defaultInvoiceDescription ?? "").trim();
    }
  }
  const uniqueDescs = [
    ...new Set(
      matches
        .map((opt) => String(opt.defaultInvoiceDescription ?? "").trim())
        .filter(Boolean)
    ),
  ];
  if (uniqueDescs.length === 1) {
    return uniqueDescs[0];
  }
  return "";
}

function getInvoiceDescriptionForContractService(contractServiceId, options) {
  const id = String(contractServiceId ?? "").trim();
  if (!id) {
    return "";
  }
  const opt = options.find((o) => String(o.contractServiceId) === id);
  return String(opt?.defaultInvoiceDescription ?? "").trim();
}

/** Distinct invoice descriptions for a services-rendered value (for disambiguation dropdown). */
function getInvoiceDescriptionChoicesForServicesRendered(servicesRendered, options) {
  const sr = String(servicesRendered ?? "").trim();
  if (!sr) {
    return [];
  }
  const seen = new Set();
  const list = [];
  for (const opt of options) {
    if (String(opt.servicesRendered ?? "").trim() !== sr) {
      continue;
    }
    const desc = String(opt.defaultInvoiceDescription ?? "").trim();
    if (!desc || seen.has(desc)) {
      continue;
    }
    seen.add(desc);
    list.push(desc);
  }
  return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** {@code act_contract_service.category} for the resolved profile service row. */
function getCategoryForProfileService(service, options) {
  const resolvedId = resolveContractServiceIdForService(service, options);
  if (resolvedId) {
    const opt = options.find((o) => String(o.contractServiceId) === String(resolvedId));
    return String(opt?.category ?? "").trim();
  }
  const sr = String(service?.servicesRendered ?? "").trim();
  if (!sr) {
    return "";
  }
  const matches = options.filter((opt) => String(opt.servicesRendered ?? "").trim() === sr);
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return String(matches[0].category ?? "").trim();
  }
  const uniqueCategories = [
    ...new Set(matches.map((opt) => String(opt.category ?? "").trim().toLowerCase()).filter(Boolean)),
  ];
  if (uniqueCategories.length === 1) {
    return String(matches[0].category ?? "").trim();
  }
  return "";
}

function isLaborCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "labor";
}

function isFlatFeeCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "flat fee";
}

/** Labor hour and Flat Fee lines do not use billing classification. */
function profileServiceOmitsBillingClassification(service, options) {
  return (
    isLaborHourService(service, options) ||
    isFlatFeeCategory(getCategoryForProfileService(service, options))
  );
}

const LABOR_HOUR_SERVICE_CODE = "LABOR_HOUR";
const LABOR_PERCENT_SERVICE_CODE = "LABOR_PERCENT";

/** {@code act_contract_service.service_code} for the resolved profile service row. */
function getServiceCodeForProfileService(service, options) {
  const resolvedId = resolveContractServiceIdForService(service, options);
  if (resolvedId) {
    const opt = options.find((o) => String(o.contractServiceId) === String(resolvedId));
    return String(opt?.serviceCode ?? "").trim();
  }
  const sr = String(service?.servicesRendered ?? "").trim();
  if (!sr) {
    return "";
  }
  const matches = options.filter((opt) => String(opt.servicesRendered ?? "").trim() === sr);
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return String(matches[0].serviceCode ?? "").trim();
  }
  const uniqueCodes = [
    ...new Set(matches.map((opt) => String(opt.serviceCode ?? "").trim().toUpperCase()).filter(Boolean)),
  ];
  if (uniqueCodes.length === 1) {
    return String(matches[0].serviceCode ?? "").trim();
  }
  return "";
}

function isLaborHourService(service, options) {
  return (
    getServiceCodeForProfileService(service, options).toUpperCase() === LABOR_HOUR_SERVICE_CODE
  );
}

function isLaborPercentService(service, options) {
  return (
    getServiceCodeForProfileService(service, options).toUpperCase() === LABOR_PERCENT_SERVICE_CODE
  );
}

function buildOpCodesWithLaborMapping(service, assignedCompanyNumbers) {
  const rows = ensureLaborMappingRows(service, assignedCompanyNumbers);
  const opCodeStrings = selectedOpCodeStrings(normalizeServiceOpCodes(service));
  const raw = buildOpCodesFromLaborMappingRows(rows, opCodeStrings, assignedCompanyNumbers);
  return normalizeOpCodesFromApi(raw);
}

/** {@code act_contract_service.client_rate} for a services-rendered dropdown value (display format). */
function getContractRateForServicesRendered(servicesRendered, options) {
  const sr = String(servicesRendered ?? "").trim();
  if (!sr) {
    return "";
  }
  const matches = options.filter(
    (opt) => String(opt.servicesRendered ?? "").trim() === sr
  );
  if (matches.length === 0) {
    return "";
  }
  if (matches.length === 1) {
    return formatContractRateDisplay(matches[0].clientRate);
  }
  const uniqueRates = [
    ...new Set(matches.map((opt) => formatContractRateDisplay(opt.clientRate))),
  ];
  if (uniqueRates.length === 1) {
    return uniqueRates[0];
  }
  return formatContractRateDisplay(matches[0].clientRate);
}

/** Form controls in the profile Add/Edit Service drawer (matches Contracts Add Service panel). */
const SERVICE_DRAWER_FIELD_BASE =
  "w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100";

function serviceDrawerFieldClassName(hasError) {
  return `${SERVICE_DRAWER_FIELD_BASE} ${
    hasError
      ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
      : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
  }`;
}

/** Invoice descriptions for profile service count hover (preserves order). */
function serviceInvoiceDescriptionsForHover(services) {
  return (services ?? []).map((s) => String(s.invoiceDescription ?? "").trim());
}

/** Hover popover on service count listing invoice descriptions. */
function ProfileServicesCountHover({ count, descriptions }) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) {
    return (
      <span className="text-sm font-bold text-red-600 dark:text-red-400">None Created</span>
    );
  }
  const lines = descriptions ?? [];
  const hasAnyDescription = lines.some((d) => d.length > 0);

  return (
    <span className="relative inline-flex group">
      <span
        className="text-sm font-semibold text-blue-600 tabular-nums underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
        aria-label={`${n} service${n === 1 ? "" : "s"}; hover for invoice descriptions`}
      >
        {n}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-30 mt-2 min-w-[14rem] max-w-sm rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible dark:border-gray-600 dark:bg-gray-900 dark:shadow-black/40"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          Invoice Description
        </p>
        <ul className="space-y-1.5 text-sm text-gray-900 dark:text-gray-100 max-h-48 overflow-y-auto">
          {!hasAnyDescription ? (
            <li className="text-gray-500 dark:text-gray-400 italic">—</li>
          ) : (
            lines.map((desc, index) => (
              <li key={`${index}-${desc}`} className="break-words">
                {desc || "—"}
              </li>
            ))
          )}
        </ul>
      </span>
    </span>
  );
}

function normalizeJobNoLookupKey(jobNo) {
  return String(jobNo ?? "").trim().toLowerCase();
}

/** Rows for assigned jackets: {@code act_inv_profile_jobno} job numbers enriched from {@code act_jobs} when present. */
function buildInvProfileJacketTableRows(jobNos, jobs) {
  const jobByNo = new Map();
  for (const job of jobs ?? []) {
    const no = String(job?.jobNo ?? job?.jobNumber ?? "").trim();
    if (!no) {
      continue;
    }
    const key = normalizeJobNoLookupKey(no);
    if (!jobByNo.has(key)) {
      jobByNo.set(key, job);
    }
  }
  return (jobNos ?? []).map((jobNo) => {
    const trimmed = String(jobNo ?? "").trim();
    const match = jobByNo.get(normalizeJobNoLookupKey(trimmed));
    const description1 = String(
      match?.description1 ?? match?.jobdesc1 ?? ""
    ).trim();
    const notes = match?.notes != null ? String(match.notes).trim() : "";
    return {
      jobNo: trimmed,
      jobType: String(match?.jobType ?? match?.type ?? "").trim(),
      status: String(match?.status ?? "").trim(),
      description1,
      notes,
    };
  });
}

/** Read-only table of jackets assigned to the invoice profile ({@code act_inv_profile_jobno}). */
function ProfileInvJobNosTable({ jobNos, jobs, isLoading, isError }) {
  const rows = useMemo(
    () => buildInvProfileJacketTableRows(jobNos, jobs),
    [jobNos, jobs]
  );

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-4">Loading jackets…</p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400 py-4">Unable to load assigned jackets.</p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 w-12">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Jacket Number
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No jackets assigned to this invoice profile.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.jobNo}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/80"
                >
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                    {index + 1}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {row.jobNo || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {row.jobType || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {row.status || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-md">
                    <span className="break-words line-clamp-3">
                      {row.description1 || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                    <span className="break-words line-clamp-2">{row.notes || "—"}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** First option in Create Profile company / jacket dropdowns (after optional empty placeholder). */
const CREATE_PROFILE_NOT_APPLICABLE = "N/A";

/** Match JacketForm / act_company_division company_number (varchar 4). */
function formatCompanyNumberPadded(v) {
  const s = String(v ?? "").trim();
  if (/^\d{1,4}$/.test(s)) {
    return s.padStart(4, "0");
  }
  return s;
}

function normalizeCreateProfileCompanyNumbers(selected) {
  const raw = [
    ...new Set(
      (selected ?? []).map((v) => String(v ?? "").trim()).filter(Boolean)
    ),
  ];
  if (raw.length === 0) {
    return [];
  }
  if (raw.includes(CREATE_PROFILE_NOT_APPLICABLE)) {
    if (raw.length === 1) {
      return [CREATE_PROFILE_NOT_APPLICABLE];
    }
    return raw
      .filter((v) => v !== CREATE_PROFILE_NOT_APPLICABLE)
      .map((v) => formatCompanyNumberPadded(v));
  }
  return raw.map((v) => formatCompanyNumberPadded(v));
}

function isCreateProfileFormComplete({
  client,
  invoiceNameProgram,
  companyNumbers,
  jackets,
  custId,
}) {
  if (custId == null) {
    return false;
  }
  if (!String(client ?? "").trim()) {
    return false;
  }
  if (!String(invoiceNameProgram ?? "").trim()) {
    return false;
  }
  if (normalizeCreateProfileCompanyNumbers(companyNumbers).length === 0) {
    return false;
  }
  const jobNos = [
    ...new Set(
      (jackets ?? []).map((jobNo) => String(jobNo ?? "").trim()).filter(Boolean)
    ),
  ];
  return jobNos.length > 0;
}

/** True when the profile has a real company # (not blank or N/A). */
function profileHasAssignedCompanyNumber(companyNumber) {
  const company = String(companyNumber ?? "").trim();
  return company.length > 0 && company !== CREATE_PROFILE_NOT_APPLICABLE;
}

function invoiceProfileHasAssignedCompanyNumber(profile) {
  if (!profile) {
    return false;
  }
  const numbers = Array.isArray(profile.companyNumbers) ? profile.companyNumbers : [];
  if (numbers.length > 0) {
    return numbers.some((n) => {
      const company = String(n ?? "").trim();
      return company.length > 0 && company !== CREATE_PROFILE_NOT_APPLICABLE;
    });
  }
  return profileHasAssignedCompanyNumber(profile.companyNumber);
}

/** Real company # values assigned to an invoice profile (excludes blank and N/A). */
function getInvoiceProfileAssignedCompanyNumbers(profile) {
  if (!profile) {
    return [];
  }
  const fromArray = Array.isArray(profile.companyNumbers) ? profile.companyNumbers : [];
  const padded = fromArray
    .map((n) => formatCompanyNumberPadded(String(n ?? "").trim()))
    .filter((n) => profileHasAssignedCompanyNumber(n));
  if (padded.length > 0) {
    return [...new Set(padded)];
  }
  const legacy = String(profile.companyNumber ?? "")
    .trim()
    .split(",")
    .map((n) => formatCompanyNumberPadded(n.trim()))
    .filter((n) => profileHasAssignedCompanyNumber(n));
  return [...new Set(legacy)];
}

function productDivisionsForComparableRow(row, productDivisionsByRowId, service) {
  const classification = String(row?.billingDataClassification ?? "").trim();
  if (classification === BILLING_DATA_CLASSIFICATION_PRODUCT_DIVISION) {
    const persisted = String(service?.billingDataClassificationValue ?? "").trim();
    if (persisted) {
      return persisted
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }
  }
  return productDivisionsByRowId[row.id] ?? [];
}

/** Fields compared when enabling Update Service (matches editable save payload). */
function buildProfileServiceFormSaveComparable(
  service,
  profile,
  contractServiceInvoiceDescriptionOptions,
  productDivisionsByRowId = {}
) {
  const serviceCategory = getCategoryForProfileService(
    service,
    contractServiceInvoiceDescriptionOptions
  );
  const laborHour = isLaborHourService(service, contractServiceInvoiceDescriptionOptions);
  const comparable = {
    servicesRendered: String(service?.servicesRendered ?? "").trim(),
    defaultInvoiceDescription: String(service?.invoiceDescription ?? "").trim(),
    sequenceDisplay: service?.sequenceDisplay ?? null,
    clientRate: contractRateForApi(service?.clientRate),
    laborPercent: laborPercentForApi(service?.laborPercent),
    effectiveDate: service?.effectiveDate ?? "",
    endDate: service?.endDate || "",
    opCodes: laborHour
      ? buildOpCodesWithLaborMapping(
          service,
          getInvoiceProfileAssignedCompanyNumbers(profile)
        )
      : isLaborCategory(serviceCategory)
        ? normalizeServiceOpCodes(service)
        : [],
  };
  if (laborHour) {
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    comparable.laborMappingRows = ensureLaborMappingRows(service, assigned).map((row) => ({
      companyNumber:
        String(row.companyNumber ?? "").trim() || defaultLaborMappingCompanyNumber(assigned),
      jobNo: String(row.jobNo ?? "").trim(),
    }));
  } else if (
    invoiceProfileHasAssignedCompanyNumber(profile) &&
    !isFlatFeeCategory(serviceCategory)
  ) {
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureBillingClassificationRows(service, assigned);
    const divisionsByRow = {};
    for (const row of rows) {
      divisionsByRow[row.id] = productDivisionsForComparableRow(
        row,
        productDivisionsByRowId,
        service
      );
    }
    comparable.billingDataClassifications = buildBillingClassificationsSaveBody(
      rows,
      divisionsByRow
    );
  }
  return comparable;
}

/** API save body for act_inv_profile_detail create/update. */
function buildProfileServiceSaveBody(
  service,
  profile,
  contractServiceInvoiceDescriptionOptions,
  productDivisionsByRow = {},
  sequenceDisplayOverride = undefined
) {
  const resolvedContractServiceId =
    resolveContractServiceIdForService(service, contractServiceInvoiceDescriptionOptions) ||
    String(service?.contractServiceId ?? "").trim();
  let defaultInvoiceDescription = String(service.invoiceDescription ?? "").trim();
  if (!defaultInvoiceDescription && resolvedContractServiceId) {
    defaultInvoiceDescription = getInvoiceDescriptionForContractService(
      resolvedContractServiceId,
      contractServiceInvoiceDescriptionOptions
    );
  }
  const serviceCategory = getCategoryForProfileService(
    service,
    contractServiceInvoiceDescriptionOptions
  );
  const laborHour = isLaborHourService(service, contractServiceInvoiceDescriptionOptions);
  const sequenceDisplay =
    sequenceDisplayOverride !== undefined
      ? sequenceDisplayOverride
      : service.sequenceDisplay ?? null;
  const body = {
    contractServiceId: Number(resolvedContractServiceId),
    servicesRendered: String(service.servicesRendered ?? "").trim(),
    defaultInvoiceDescription,
    sequenceDisplay,
    clientRate: contractRateForApi(service.clientRate),
    laborPercent: laborPercentForApi(service.laborPercent),
    effectiveDate: service.effectiveDate,
    endDate: service.endDate || "",
    opCodes: laborHour
      ? buildOpCodesWithLaborMapping(
          service,
          getInvoiceProfileAssignedCompanyNumbers(profile)
        )
      : isLaborCategory(serviceCategory)
        ? normalizeServiceOpCodes(service)
        : [],
  };
  if (
    invoiceProfileHasAssignedCompanyNumber(profile) &&
    !laborHour &&
    !isFlatFeeCategory(serviceCategory)
  ) {
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureBillingClassificationRows(service, assigned);
    const classifications = rows
      .map((row) => ({
        companyNumber: String(row.companyNumber ?? "").trim(),
        billingDataClassification: String(row.billingDataClassification ?? "").trim(),
        billingDataClassificationValue: resolveRowBillingDataClassificationAttributeValue(
          row,
          productDivisionsForComparableRow(row, productDivisionsByRow, service)
        ),
      }))
      .filter(
        (entry) => entry.billingDataClassification && entry.billingDataClassificationValue
      );
    body.billingDataClassifications = classifications;
    if (classifications.length > 0) {
      const first = classifications[0];
      body.billingDataClassification = first.billingDataClassification;
      body.billingDataClassificationValue = first.billingDataClassificationValue;
      body.companyNumber = first.companyNumber;
    }
  }
  return body;
}

/** True when persisted billing classification exists for display in the services table. */
function serviceHasBillingClassificationData(service) {
  return serviceHasBillingClassificationRows(service);
}

const defaultClients = [
  { id: 1, name: "Acme Corporation", code: "ACME", status: "Active" },
  { id: 2, name: "Global Tech Inc", code: "GLBL", status: "Active" },
  { id: 3, name: "Stellar Industries", code: "STLR", status: "Active" }
];
function BillingProfileConfiguration({
  embedded = false,
  createModalOnly = false,
  createModalOpen = false,
  onCreateModalOpenChange,
  custId: custIdProp = null,
  clientName: clientNameProp = "",
  clientCode: clientCodeProp = "",
  focusInvProfileId = null,
  onCloseEmbedded,
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientCodeFromUrl = (
    embedded ? clientCodeProp : (searchParams.get("clientCode") ?? "")
  ).trim();
  const clientNameFromUrl = (
    embedded ? clientNameProp : (searchParams.get("clientName") ?? "")
  ).trim();
  const custIdForQuery = useMemo(() => {
    const fromProp = Number(custIdProp);
    if (Number.isFinite(fromProp) && fromProp > 0) {
      return fromProp;
    }
    const q = Number.parseInt(searchParams.get("custId") ?? "", 10);
    if (Number.isFinite(q) && q > 0) {
      return q;
    }
    const p = readInvoiceProfileClientFromSession();
    const c = Number.parseInt(String(p?.custId ?? ""), 10);
    if (Number.isFinite(c) && c > 0) {
      return c;
    }
    return null;
  }, [custIdProp, searchParams]);

  const focusProfileIdNormalized = useMemo(
    () => normalizeProfileId(focusInvProfileId),
    [focusInvProfileId]
  );

  const skipInvProfileJobnosQuery =
    !embedded ||
    custIdForQuery == null ||
    focusProfileIdNormalized == null;

  const {
    data: focusedInvProfileJobNos = [],
    isLoading: isFocusedInvProfileJobNosLoading,
    isError: isFocusedInvProfileJobNosError,
  } = useGetClientConfigurationCustomerInvProfileJobnosQuery(
    { custId: custIdForQuery, invProfileId: focusProfileIdNormalized },
    { skip: skipInvProfileJobnosQuery }
  );

  const auth = useAppSelector(selectAuth);
  const { data: employees = [] } = useGetClientConfigurationEmployeesQuery();

  const {
    data: companyNumberOptionsRaw = [],
    isLoading: isCompanyNumbersLoading,
    isError: isCompanyNumbersError,
  } = useGetClientConfigurationCustomerCompanyNumbersQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const normalizedCompanyNumberOptions = useMemo(() => {
    if (!Array.isArray(companyNumberOptionsRaw) || companyNumberOptionsRaw.length === 0) {
      return [];
    }
    const set = new Set();
    for (const v of companyNumberOptionsRaw) {
      const p = formatCompanyNumberPadded(String(v ?? "").trim());
      if (p) {
        set.add(p);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [companyNumberOptionsRaw]);

  const {
    data: jacketJobsPayload,
    isLoading: isJacketJobsForProfileLoading,
    isError: isJacketJobsForProfileError,
  } = useGetClientConfigurationCustomerJacketJobsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const {
    data: poDropdownOptionsRaw = [],
    isLoading: isPoDropdownLoading,
    isError: isPoDropdownError,
  } = useGetClientConfigurationCustomerPoOptionsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const poDropdownOptions = useMemo(() => {
    const arr = Array.isArray(poDropdownOptionsRaw) ? poDropdownOptionsRaw : [];
    return arr
      .map((row) => {
        const poId = row?.poId ?? row?.po_id;
        const clientPo = String(row?.clientPo ?? row?.client_po ?? "").trim();
        if (poId == null || poId === "" || !clientPo) {
          return null;
        }
        return { poId: String(poId), clientPo };
      })
      .filter(Boolean);
  }, [poDropdownOptionsRaw]);

  const {
    data: invProfilesFromApi = [],
    isSuccess: isInvProfilesLoaded,
    isFetching: isInvProfilesFetching,
  } = useGetClientConfigurationCustomerInvProfilesQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const {
    data: invProfileDetailsFromApi = [],
    isSuccess: isInvProfileDetailsLoaded,
    isFetching: isInvProfileDetailsFetching,
  } = useGetClientConfigurationCustomerInvProfileDetailsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const isProfilesLoading =
    custIdForQuery != null &&
    (isInvProfilesFetching || !isInvProfilesLoaded || isInvProfileDetailsFetching);

  const [createInvProfile, { isLoading: isCreateProfileSaving }] =
    useCreateClientConfigurationInvProfileMutation();

  const [createInvProfileDetail, { isLoading: isCreatingInvProfileDetail }] =
    useCreateClientConfigurationInvProfileDetailMutation();
  const [updateInvProfileDetail, { isLoading: isUpdatingInvProfileDetail }] =
    useUpdateClientConfigurationInvProfileDetailMutation();
  const [endInvProfileDetail, { isLoading: isEndingInvProfileDetail }] =
    useEndClientConfigurationInvProfileDetailMutation();
  const [deactivateInvProfile, { isLoading: isDeactivatingInvProfile }] =
    useDeactivateClientConfigurationInvProfileMutation();
  const isServiceSaving = isCreatingInvProfileDetail || isUpdatingInvProfileDetail;

  const loggedInEmployeeDisplayName = useMemo(() => {
    const given = String(auth?.decodedToken?.given_name ?? "").trim();
    const family = String(auth?.decodedToken?.family_name ?? "").trim();
    if (given || family) {
      return [given, family].filter(Boolean).join(" ");
    }
    const sub = auth?.decodedToken?.sub;
    const empId =
      sub != null && /^\d+$/.test(String(sub).trim()) ? String(sub).trim() : "";
    if (empId) {
      const match = employees.find((e) => String(e.empId) === empId);
      if (match?.displayName?.trim()) {
        return match.displayName.trim();
      }
    }
    return String(auth?.decodedToken?.name ?? "").trim();
  }, [
    auth?.decodedToken?.sub,
    auth?.decodedToken?.name,
    auth?.decodedToken?.given_name,
    auth?.decodedToken?.family_name,
    employees,
  ]);

  const {
    data: contractServiceInvoiceDescriptionOptions = [],
    isLoading: isInvoiceDescriptionOptionsLoading,
    isError: isInvoiceDescriptionOptionsError,
  } = useGetClientConfigurationContractServiceInvoiceDescriptionOptionsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });

  const {
    data: operationOptions = [],
    isLoading: isOperationsLoading,
    isError: isOperationsError,
  } = useGetClientConfigurationOperationsQuery();

  const operationLabelByCode = useMemo(() => {
    const map = new Map();
    for (const opt of operationOptions) {
      const code = String(opt.opCode ?? "").trim();
      if (!code) {
        continue;
      }
      map.set(code, opt.descrip ? `${code} — ${opt.descrip}` : code);
    }
    return map;
  }, [operationOptions]);

  const contractServicesRenderedOptions = useMemo(() => {
    const today = todayIsoDate();
    const seen = new Set();
    const list = [];
    for (const opt of contractServiceInvoiceDescriptionOptions) {
      if (!isContractServiceActiveForServicesRenderedDropdown(opt, today)) {
        continue;
      }
      const label = String(opt.servicesRendered ?? "").trim();
      if (!label || seen.has(label)) {
        continue;
      }
      seen.add(label);
      list.push(label);
    }
    return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [contractServiceInvoiceDescriptionOptions]);

  const companySelectOptions = useMemo(() => {
    const rest = normalizedCompanyNumberOptions.filter((x) => x !== CREATE_PROFILE_NOT_APPLICABLE);
    return [CREATE_PROFILE_NOT_APPLICABLE, ...rest];
  }, [normalizedCompanyNumberOptions]);

  const jacketJobNoOptionsForCreateProfile = useMemo(() => {
    const set = new Set();
    for (const job of jacketJobsPayload?.jobs ?? []) {
      const no = String(job?.jobNo ?? job?.jobNumber ?? "").trim();
      if (no) {
        set.add(no);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [jacketJobsPayload]);

  const jacketMultiselectSize = useMemo(
    () => Math.min(8, Math.max(3, jacketJobNoOptionsForCreateProfile.length)),
    [jacketJobNoOptionsForCreateProfile.length]
  );

  const companyMultiselectSize = useMemo(
    () => Math.min(8, Math.max(3, companySelectOptions.length)),
    [companySelectOptions.length]
  );

  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const profilesRef = useRef(profiles);
  const serviceOrderDragSnapshotRef = useRef(null);
  const [isSavingServiceOrder, setIsSavingServiceOrder] = useState(false);
  const [serviceOrderSaveError, setServiceOrderSaveError] = useState("");
  const [showCreateModalInternal, setShowCreateModalInternal] = useState(false);
  const createModalControlled = typeof onCreateModalOpenChange === "function";
  const showCreateModal = createModalControlled ? !!createModalOpen : showCreateModalInternal;
  const prevCreateModalOpenRef = useRef(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [newProfileClient, setNewProfileClient] = useState("");
  const [newProfileCompanyNumbers, setNewProfileCompanyNumbers] = useState([]);
  const [newProfileJackets, setNewProfileJackets] = useState([]);
  const [newProfilePoId, setNewProfilePoId] = useState("");
  const [newProfileInvoiceNameProgram, setNewProfileInvoiceNameProgram] = useState("");
  const [createProfileErrors, setCreateProfileErrors] = useState({});
  const [createProfileFocusField, setCreateProfileFocusField] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [expandedProfileServiceKey, setExpandedProfileServiceKey] = useState(null);
  const [serviceDrawerProfileId, setServiceDrawerProfileId] = useState(null);
  const [serviceSaveErrors, setServiceSaveErrors] = useState({});
  const [serviceSaveSuccess, setServiceSaveSuccess] = useState({});
  const billingLookupValidationByRowRef = useRef({});
  const productDivisionsByRowRef = useRef({});
  const profileServiceEditBaselineRef = useRef(null);
  const profileServiceEditSessionRef = useRef(null);
  const [profileServiceEditBaselineReady, setProfileServiceEditBaselineReady] = useState(false);
  const [billingLookupRevision, setBillingLookupRevision] = useState(0);
  const [productDivisionsRevision, setProductDivisionsRevision] = useState(0);
  const [pendingRemoveService, setPendingRemoveService] = useState(null);
  const [pendingDeactivateProfileId, setPendingDeactivateProfileId] = useState(null);
  const [deactivateProfileError, setDeactivateProfileError] = useState("");
  const [endServiceError, setEndServiceError] = useState("");
  const [billingProfileStatusFilter, setBillingProfileStatusFilter] = useState(
    BILLING_PROFILE_STATUS_FILTER_ALL
  );
  const [billingProfileCompanyFilter, setBillingProfileCompanyFilter] = useState(
    BILLING_PROFILE_COMPANY_FILTER_ALL
  );
  const createModalClientFocusRef = useRef(null);
  const createModalInvoiceNameProgramRef = useRef(null);
  const createModalCompanyNumberRef = useRef(null);
  const createModalJacketRef = useRef(null);

  const openCreateProfileModal = useCallback(() => {
    setCreateProfileErrors({});
    setNewProfileClient(
      (clientCodeFromUrl || selectedClient || "").trim() ||
        (clients.length > 0 ? String(clients[0].code ?? "").trim() : "") ||
        ""
    );
    if (createModalControlled) {
      onCreateModalOpenChange(true);
    } else {
      setShowCreateModalInternal(true);
    }
  }, [
    clientCodeFromUrl,
    selectedClient,
    clients,
    createModalControlled,
    onCreateModalOpenChange,
  ]);

  const closeCreateProfileModal = useCallback(() => {
    setNewProfileClient("");
    setNewProfileCompanyNumbers([]);
    setNewProfileJackets([]);
    setNewProfilePoId("");
    setNewProfileInvoiceNameProgram("");
    setCreateProfileErrors({});
    if (createModalControlled) {
      onCreateModalOpenChange(false);
    } else {
      setShowCreateModalInternal(false);
    }
  }, [createModalControlled, onCreateModalOpenChange]);

  useEffect(() => {
    if (!createModalControlled) {
      return;
    }
    if (createModalOpen && !prevCreateModalOpenRef.current) {
      setCreateProfileErrors({});
      setNewProfileClient(
        (clientCodeFromUrl || selectedClient || "").trim() ||
          (clients.length > 0 ? String(clients[0].code ?? "").trim() : "") ||
          ""
      );
    }
    prevCreateModalOpenRef.current = createModalOpen;
  }, [
    createModalControlled,
    createModalOpen,
    clientCodeFromUrl,
    selectedClient,
    clients,
  ]);

  useEffect(() => {
    if (!embedded || !clientCodeFromUrl) {
      return;
    }
    setSelectedClient(clientCodeFromUrl);
  }, [embedded, clientCodeFromUrl]);

  useEffect(() => {
    const savedClients = localStorage.getItem("clients");
    if (savedClients) {
      const parsedClients = JSON.parse(savedClients);
      const existingIds = new Set(parsedClients.map((c) => c.id));
      const defaultsNotInSaved = defaultClients.filter((c) => !existingIds.has(c.id));
      const allClients = [...parsedClients, ...defaultsNotInSaved];
      setClients(allClients);
    } else {
      setClients(defaultClients);
    }
  }, []);
  useEffect(() => {
    if (embedded) {
      return;
    }
    if (custIdForQuery != null) {
      const persisted = readInvoiceProfileClientFromSession();
      writeInvoiceProfileClientToSession({
        custId: custIdForQuery,
        clientName: clientNameFromUrl || persisted?.clientName || "",
        clientCode: clientCodeFromUrl || persisted?.clientCode || "",
      });
      if (!searchParams.get("custId")) {
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.set("custId", String(custIdForQuery));
            return n;
          },
          { replace: true }
        );
      }
      return;
    }
    if (clients.length === 0) {
      return;
    }
    const persisted = readInvoiceProfileClientFromSession();
    let codeHint = clientCodeFromUrl;
    let nameHint = clientNameFromUrl;
    let usedPersistedHints = false;
    if (!codeHint && !nameHint) {
      if (persisted && (persisted.clientCode || persisted.clientName)) {
        codeHint = (persisted.clientCode ?? "").trim();
        nameHint = (persisted.clientName ?? "").trim();
        usedPersistedHints = true;
      }
    }
    let next = "";
    if (codeHint) {
      const byCode = clients.find(
        (c) => String(c.code ?? "").toUpperCase() === codeHint.toUpperCase()
      );
      if (byCode) {
        next = byCode.code;
      }
    }
    if (!next && nameHint) {
      const lowered = nameHint.toLowerCase();
      const byName = clients.find(
        (c) => (c.name || "").trim().toLowerCase() === lowered
      );
      if (byName) {
        next = byName.code;
      }
    }
    if (!next && codeHint) {
      next = codeHint;
    }
    if (!next && !codeHint && !nameHint && clients.length > 0) {
      next = clients[0].code;
    }
    setSelectedClient(next);

    const resolved = clients.find((c) => c.code === next);
    const saveCode = String(resolved?.code ?? "").trim();
    const saveName = String(resolved?.name ?? "").trim();

    const urlCustId = Number.parseInt(searchParams.get("custId") ?? "", 10);
    const urlCustIdOk = Number.isFinite(urlCustId) && urlCustId > 0;
    const persistedCustId =
      persisted?.custId != null &&
      Number.isFinite(Number(persisted.custId)) &&
      Number(persisted.custId) > 0
        ? Number(persisted.custId)
        : null;
    const custIdForWrite = urlCustIdOk ? urlCustId : persistedCustId ?? undefined;

    const cameFromUrl = !!(clientCodeFromUrl || clientNameFromUrl);
    if (cameFromUrl || usedPersistedHints) {
      writeInvoiceProfileClientToSession({
        clientCode: saveCode || codeHint,
        clientName: saveName || nameHint,
        custId: custIdForWrite,
      });
    }

    const shouldHydrateUrl =
      usedPersistedHints &&
      !clientCodeFromUrl &&
      !clientNameFromUrl &&
      (saveCode || saveName || nameHint || codeHint);
    const shouldHydrateCustId =
      !searchParams.get("custId") &&
      persistedCustId != null &&
      (!!clientCodeFromUrl || !!clientNameFromUrl || usedPersistedHints);

    if (shouldHydrateUrl || shouldHydrateCustId) {
      setSearchParams((prev) => {
        const n = new URLSearchParams(prev);
        if (shouldHydrateUrl) {
          const c = saveCode || codeHint;
          const nm = saveName || nameHint;
          if (c) {
            n.set("clientCode", c);
          }
          if (nm) {
            n.set("clientName", nm);
          }
        }
        if (shouldHydrateCustId) {
          n.set("custId", String(persistedCustId));
        }
        return n;
      }, { replace: true });
    }
  }, [embedded, clients, clientCodeFromUrl, clientNameFromUrl, searchParams, setSearchParams, custIdForQuery]);
  const clientCodeHint = useMemo(
    () => (selectedClient || clientCodeFromUrl || "").trim(),
    [selectedClient, clientCodeFromUrl]
  );

  useEffect(() => {
    if (custIdForQuery == null) {
      setProfiles([]);
      return;
    }
    if (!isInvProfilesLoaded) {
      return;
    }
    setProfiles((prev) => {
      const unsavedByProfile = new Map();
      for (const p of prev) {
        const pid = normalizeProfileId(p.id);
        const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
        if (pid != null && unsaved.length) {
          unsavedByProfile.set(pid, unsaved);
        }
      }
      return invProfilesFromApi.map((row) => {
        const ui = mapInvProfileRowToUi(row);
        if (!ui.clientCode && clientCodeHint) {
          ui.clientCode = clientCodeHint;
        }
        const profileId = normalizeProfileId(ui.id);
        const prevProfile = prev.find((p) => normalizeProfileId(p.id) === profileId);
        const unsaved = profileId != null ? (unsavedByProfile.get(profileId) ?? []) : [];
        const savedFromPrev = sortSavedProfileServicesNewestFirst(
          (prevProfile?.services ?? []).filter((s) => s.invProfileDetailId)
        );
        const expandedDefault =
          embedded && focusProfileIdNormalized != null && profileId === focusProfileIdNormalized;
        return {
          ...ui,
          isExpanded: embedded
            ? expandedDefault || (prevProfile?.isExpanded ?? false)
            : (prevProfile?.isExpanded ?? false),
          services: [...unsaved, ...savedFromPrev],
        };
      });
    });
  }, [
    custIdForQuery,
    invProfilesFromApi,
    isInvProfilesLoaded,
    clientCodeHint,
    embedded,
    focusProfileIdNormalized,
  ]);

  useEffect(() => {
    if (!embedded || focusProfileIdNormalized == null) {
      return;
    }
    setProfiles((prev) =>
      prev.map((p) =>
        normalizeProfileId(p.id) === focusProfileIdNormalized
          ? { ...p, isExpanded: true }
          : p
      )
    );
  }, [embedded, focusProfileIdNormalized, isInvProfilesLoaded]);

  const billingProfileCompanyFilterOptions = useMemo(() => {
    if (embedded) {
      return [BILLING_PROFILE_COMPANY_FILTER_ALL];
    }
    const set = new Set();
    for (const profile of profiles) {
      for (const companyNumber of getInvoiceProfileAssignedCompanyNumbers(profile)) {
        set.add(companyNumber);
      }
    }
    return [
      BILLING_PROFILE_COMPANY_FILTER_ALL,
      ...[...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    ];
  }, [profiles, embedded]);

  const profilesToDisplay = useMemo(() => {
    let list = profiles;
    if (focusProfileIdNormalized != null) {
      list = list.filter(
        (p) => normalizeProfileId(p.id) === focusProfileIdNormalized
      );
    }
    if (!embedded) {
      if (billingProfileStatusFilter === BILLING_PROFILE_STATUS_FILTER_ACTIVE) {
        list = list.filter((profile) => profile.activeFlag);
      } else if (billingProfileStatusFilter === BILLING_PROFILE_STATUS_FILTER_DEACTIVE) {
        list = list.filter((profile) => !profile.activeFlag);
      }
      if (billingProfileCompanyFilter !== BILLING_PROFILE_COMPANY_FILTER_ALL) {
        list = list.filter((profile) =>
          getInvoiceProfileAssignedCompanyNumbers(profile).includes(
            billingProfileCompanyFilter
          )
        );
      }
    }
    return list;
  }, [
    profiles,
    focusProfileIdNormalized,
    embedded,
    billingProfileStatusFilter,
    billingProfileCompanyFilter,
  ]);

  useEffect(() => {
    if (!billingProfileCompanyFilterOptions.includes(billingProfileCompanyFilter)) {
      setBillingProfileCompanyFilter(BILLING_PROFILE_COMPANY_FILTER_ALL);
    }
  }, [billingProfileCompanyFilter, billingProfileCompanyFilterOptions]);

  useEffect(() => {
    if (custIdForQuery == null || !isInvProfileDetailsLoaded) {
      return;
    }
    const assignedByProfileId = new Map();
    for (const profileRow of invProfilesFromApi) {
      const profilePid = normalizeProfileId(
        profileRow.invProfileId ?? profileRow.inv_profile_id
      );
      if (profilePid != null) {
        assignedByProfileId.set(
          profilePid,
          getInvoiceProfileAssignedCompanyNumbers(mapInvProfileRowToUi(profileRow))
        );
      }
    }
    const detailsByProfileId = new Map();
    for (const row of invProfileDetailsFromApi) {
      const pid = normalizeProfileId(row.invProfileId ?? row.inv_profile_id);
      if (pid == null) {
        continue;
      }
      if (!detailsByProfileId.has(pid)) {
        detailsByProfileId.set(pid, []);
      }
      detailsByProfileId
        .get(pid)
        .push(
          mapInvProfileDetailRowToUi(row, assignedByProfileId.get(pid) ?? [])
        );
    }
    setProfiles((prev) =>
      prev.map((p) => {
        const profileId = normalizeProfileId(p.id);
        if (profileId == null) {
          return p;
        }
        const saved = sortSavedProfileServicesNewestFirst(
          detailsByProfileId.get(profileId) ?? []
        );
        const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
        return {
          ...p,
          isExpanded: p.isExpanded,
          services: [...unsaved, ...saved],
        };
      })
    );
  }, [
    custIdForQuery,
    invProfileDetailsFromApi,
    invProfilesFromApi,
    isInvProfileDetailsLoaded,
  ]);

  useEffect(() => {
    if (custIdForQuery != null) {
      return;
    }
    const storageProfiles = profiles.map((profile) => {
      const client = clients.find((c) => c.code === profile.clientCode);
      return {
      id: profile.id,
      clientId: client?.id || 0,
      clientName: client?.name || "",
      clientCode: profile.clientCode || "",
      profileName: profile.invoiceNameProgram || profile.companyNumber || "Invoice profile",
      services: profile.services.map((s) => ({
        ...s,
        companyNumber: profile.companyNumber,
        programName: profile.invoiceNameProgram || profile.description,
        serviceType: "Activity Based",
        unitType: s.basedOn,
        rate: s.clientRate
      })),
      status: "Active",
      createdDate: (/* @__PURE__ */ new Date()).toISOString().split("T")[0],
      companyNumber: profile.companyNumber,
      description: profile.description,
      invoiceNameProgram: profile.invoiceNameProgram || ""
    };
    });
    localStorage.setItem("billingProfiles", JSON.stringify(storageProfiles));
  }, [profiles, clients, custIdForQuery]);
  useEffect(() => {
    setNewProfileJackets((prev) => {
      const allowed = new Set(jacketJobNoOptionsForCreateProfile);
      const next = prev.filter((jobNo) => allowed.has(jobNo));
      return next.length === prev.length ? prev : next;
    });
  }, [jacketJobNoOptionsForCreateProfile]);
  useEffect(() => {
    setNewProfileCompanyNumbers((prev) => {
      const allowed = new Set(companySelectOptions);
      const next = prev.filter((companyNumber) => allowed.has(companyNumber));
      return next.length === prev.length ? prev : next;
    });
  }, [companySelectOptions]);
  useLayoutEffect(() => {
    if (!showCreateModal || !createProfileFocusField) {
      return;
    }
    const refs = {
      client: createModalClientFocusRef,
      invoiceNameProgram: createModalInvoiceNameProgramRef,
      companyNumber: createModalCompanyNumberRef,
      jacket: createModalJacketRef,
      jobNo: createModalJacketRef,
    };
    refs[createProfileFocusField]?.current?.focus?.();
    setCreateProfileFocusField(null);
  }, [showCreateModal, createProfileFocusField]);
  const createProfile = async () => {
    const errors = {};
    if (!(newProfileClient || "").trim()) {
      errors.client = "Client is required.";
    }
    const invoiceProgram = (newProfileInvoiceNameProgram || "").trim();
    if (!invoiceProgram) {
      errors.invoiceNameProgram = "Invoice Name/Program is required.";
    }
    if (normalizeCreateProfileCompanyNumbers(newProfileCompanyNumbers).length === 0) {
      errors.companyNumber = "Company # is required.";
    }
    if (newProfileJackets.length === 0) {
      errors.jacket = "At least one jacket is required.";
    }
    if (Object.keys(errors).length > 0) {
      setCreateProfileErrors(errors);
      const order = ["client", "invoiceNameProgram", "companyNumber", "jacket"];
      setCreateProfileFocusField(order.find((k) => errors[k]) ?? null);
      return;
    }
    if (custIdForQuery == null) {
      setCreateProfileErrors({
        _global: "Create profile requires an existing client. Open this page from Client Configuration.",
      });
      return;
    }
    setCreateProfileErrors({});
    const companyNumbersStored = normalizeCreateProfileCompanyNumbers(newProfileCompanyNumbers);
    if (companyNumbersStored.length === 0) {
      setCreateProfileErrors({ companyNumber: "Company # is required." });
      setCreateProfileFocusField("companyNumber");
      return;
    }
    const jobNosStored = [
      ...new Set(
        newProfileJackets.map((jobNo) => String(jobNo ?? "").trim()).filter(Boolean)
      ),
    ];
    if (jobNosStored.length === 0) {
      setCreateProfileErrors({ jacket: "At least one jacket is required." });
      setCreateProfileFocusField("jacket");
      return;
    }
    const poIdRaw = String(newProfilePoId ?? "").trim();
    const poIdParsed = poIdRaw ? Number.parseInt(poIdRaw, 10) : null;
    const poIdBody =
      poIdParsed != null && Number.isInteger(poIdParsed) && poIdParsed > 0 ? poIdParsed : null;
    try {
      await createInvProfile({
        custId: custIdForQuery,
        body: {
          invoiceNameProgram: invoiceProgram,
          companyNumbers: companyNumbersStored,
          jobNos: jobNosStored,
          poId: poIdBody,
        },
      }).unwrap();
      closeCreateProfileModal();
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const mapped = {};
        for (const [k, v] of Object.entries(data.errors)) {
          mapped[k] = typeof v === "string" ? v : String(v);
        }
        setCreateProfileErrors(mapped);
        const order = ["client", "invoiceNameProgram", "companyNumber", "jacket", "jobNo", "jobNos", "poId", "_global"];
        setCreateProfileFocusField(order.find((k) => mapped[k]) ?? null);
        return;
      }
      const status = err?.status;
      let message = data?.detail || data?.title || "Unable to save invoice profile.";
      if (status === 404 || /not found/i.test(String(message))) {
        message =
          "Invoice profile API was not found (404). If you run the backend on this PC, use .env.local with REACT_APP_API_BASE_URL=http://localhost and REACT_APP_API_PORT=10605, then restart npm start.";
      }
      setCreateProfileErrors({ _global: message });
    }
  };
  const toggleProfileExpanded = (profileId) => {
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) {
      return;
    }
    const nextExpanded = !profile.isExpanded;
    if (nextExpanded) {
      setProfiles((prev) =>
        prev.map((p) => (p.id === profileId ? { ...p, isExpanded: true } : p))
      );
      return;
    }

    const unsavedServiceIds = (profile.services ?? [])
      .filter((s) => !s.invProfileDetailId)
      .map((s) => s.id);

    setProfiles((prev) =>
      prev.map((p) =>
        p.id === profileId
          ? {
              ...p,
              isExpanded: false,
              services: (p.services ?? []).filter((s) => s.invProfileDetailId),
            }
          : p
      )
    );
    setExpandedProfileServiceKey((current) =>
      current != null && String(current).startsWith(`${profileId}-`) ? null : current
    );

    if (unsavedServiceIds.length > 0) {
      setEditingServiceId((current) =>
        unsavedServiceIds.includes(current) ? null : current
      );
      setServiceSaveErrors((prev) => {
        const next = { ...prev };
        unsavedServiceIds.forEach((id) => delete next[id]);
        return next;
      });
      setServiceSaveSuccess((prev) => {
        const next = { ...prev };
        unsavedServiceIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  const moveProfileService = useCallback((profileId, dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) {
      return;
    }
    setProfiles((prev) =>
      prev.map((p) => {
        if (!profileIdsMatch(p.id, profileId)) {
          return p;
        }
        const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
        const saved = savedProfileServices(p.services);
        const reordered = [...saved];
        const [moved] = reordered.splice(dragIndex, 1);
        if (!moved) {
          return p;
        }
        reordered.splice(hoverIndex, 0, moved);
        const withSequence = reordered.map((service, idx) => ({
          ...service,
          sequenceDisplay: idx + 1,
        }));
        return { ...p, services: [...unsaved, ...withSequence] };
      })
    );
  }, []);

  useEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  const handleServiceDragStart = useCallback((profileId) => {
    setServiceOrderSaveError("");
    if (serviceOrderDragSnapshotRef.current?.profileId === profileId) {
      return;
    }
    const profile = profilesRef.current.find((p) => profileIdsMatch(p.id, profileId));
    if (!profile) {
      return;
    }
    const saved = savedProfileServices(profile.services);
    serviceOrderDragSnapshotRef.current = {
      profileId,
      sequences: Object.fromEntries(
        saved.map((service) => [
          String(service.invProfileDetailId),
          normalizedSequenceDisplayValue(service.sequenceDisplay),
        ])
      ),
    };
  }, []);

  const restoreProfileServiceOrderFromSnapshot = useCallback((profileId, snapshot) => {
    if (!snapshot) {
      return;
    }
    setProfiles((prev) =>
      prev.map((p) => {
        if (!profileIdsMatch(p.id, profileId)) {
          return p;
        }
        const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
        const saved = savedProfileServices(p.services);
        const restored = [...saved]
          .sort((a, b) => {
            const aSeq =
              snapshot.sequences[String(a.invProfileDetailId)] ?? Number.MAX_SAFE_INTEGER;
            const bSeq =
              snapshot.sequences[String(b.invProfileDetailId)] ?? Number.MAX_SAFE_INTEGER;
            if (aSeq !== bSeq) {
              return aSeq - bSeq;
            }
            return Number(b.invProfileDetailId) - Number(a.invProfileDetailId);
          })
          .map((service) => ({
            ...service,
            sequenceDisplay:
              snapshot.sequences[String(service.invProfileDetailId)] ?? service.sequenceDisplay,
          }));
        return { ...p, services: [...unsaved, ...restored] };
      })
    );
  }, []);

  const persistProfileServiceOrder = useCallback(
    async (profileId) => {
      const snapshot = serviceOrderDragSnapshotRef.current;
      serviceOrderDragSnapshotRef.current = null;
      if (!snapshot || !profileIdsMatch(snapshot.profileId, profileId)) {
        return;
      }
      if (custIdForQuery == null) {
        return;
      }
      const profile = profilesRef.current.find((p) => profileIdsMatch(p.id, profileId));
      if (!profile) {
        return;
      }
      const saved = savedProfileServices(profile.services);
      const updates = saved
        .map((service, index) => ({
          service,
          sequenceDisplay: index + 1,
        }))
        .filter(({ service, sequenceDisplay }) => {
          const previous = snapshot.sequences[String(service.invProfileDetailId)];
          return previous !== sequenceDisplay;
        });
      if (updates.length === 0) {
        return;
      }
      setServiceOrderSaveError("");
      setIsSavingServiceOrder(true);
      try {
        await Promise.all(
          updates.map(({ service, sequenceDisplay }) => {
            const body = buildProfileServiceSaveBody(
              service,
              profile,
              contractServiceInvoiceDescriptionOptions,
              productDivisionsByRowRef.current,
              sequenceDisplay
            );
            if (!body.contractServiceId || body.contractServiceId <= 0) {
              const validationError = new Error("Contract service is required.");
              validationError.data = {
                errors: {
                  contractServiceId: "Contract service is required.",
                },
              };
              throw validationError;
            }
            return updateInvProfileDetail({
              custId: custIdForQuery,
              invProfileId: profile.id,
              invProfileDetailId: Number(service.invProfileDetailId),
              body,
            }).unwrap();
          })
        );
      } catch (err) {
        restoreProfileServiceOrderFromSnapshot(profileId, snapshot);
        const message = formatProfileServiceApiError(
          err?.data,
          "Unable to save service display order."
        );
        setServiceOrderSaveError(message);
      } finally {
        setIsSavingServiceOrder(false);
      }
    },
    [
      contractServiceInvoiceDescriptionOptions,
      custIdForQuery,
      restoreProfileServiceOrderFromSnapshot,
      updateInvProfileDetail,
    ]
  );

  const handleServiceDragEnd = useCallback(
    (profileId) => {
      void persistProfileServiceOrder(profileId);
    },
    [persistProfileServiceOrder]
  );

  const requestDeactivateProfile = (profileId) => {
    setDeactivateProfileError("");
    setPendingDeactivateProfileId(profileId);
  };

  const handleCancelDeactivateProfile = () => {
    if (isDeactivatingInvProfile) {
      return;
    }
    setPendingDeactivateProfileId(null);
    setDeactivateProfileError("");
  };

  const handleConfirmDeactivateProfile = async () => {
    if (pendingDeactivateProfileId == null) {
      return;
    }
    const profileId = pendingDeactivateProfileId;
    const profile = profiles.find(
      (p) => normalizeProfileId(p.id) === normalizeProfileId(profileId)
    );
    if (!profile) {
      setPendingDeactivateProfileId(null);
      return;
    }
    if (custIdForQuery == null) {
      setDeactivateProfileError(
        "Deactivating a profile requires an existing client. Open this page from Client Configuration."
      );
      return;
    }
    setDeactivateProfileError("");
    try {
      await deactivateInvProfile({
        custId: custIdForQuery,
        invProfileId: Number(profile.id),
      }).unwrap();
      const today = todayIsoDate();
      setProfiles((prev) =>
        prev.map((p) => {
          if (normalizeProfileId(p.id) !== normalizeProfileId(profileId)) {
            return p;
          }
          const endedServices = (p.services ?? []).map((s) =>
            s.invProfileDetailId && !serviceHasEndDate(s)
              ? {
                  ...s,
                  endDate: today,
                  isEnded: true,
                  endAppliedByDisplayName: loggedInEmployeeDisplayName,
                }
              : s
          );
          return { ...p, activeFlag: false, services: endedServices };
        })
      );
      setPendingDeactivateProfileId(null);
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const first = Object.values(data.errors).find((v) => v != null && String(v).trim());
        setDeactivateProfileError(
          first != null ? String(first).trim() : "Unable to deactivate invoice profile."
        );
        return;
      }
      const status = err?.status;
      let message = data?.detail || data?.title || "Unable to deactivate invoice profile.";
      if (status === 404 || /not found/i.test(String(message))) {
        message =
          "Invoice profile deactivate API was not found (404). If you run the backend on this PC, use .env.local with REACT_APP_API_BASE_URL=http://localhost and REACT_APP_API_PORT=10605, then restart npm start.";
      }
      setDeactivateProfileError(message);
    }
  };
  const openProfileServiceDrawer = (profileId, serviceId) => {
    setServiceDrawerProfileId(profileId);
    profileServiceEditBaselineRef.current = null;
    profileServiceEditSessionRef.current = null;
    setProfileServiceEditBaselineReady(false);
    setEditingServiceId(serviceId);
    setServiceSaveSuccess((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  };

  const addServiceToProfile = (profileId) => {
    const profile = profiles.find((p) => p.id === profileId);
    if (profile && profileHasUnsavedService(profile)) {
      return;
    }
    const assignedCompanyNumbers = getInvoiceProfileAssignedCompanyNumbers(profile);
    const newService = {
      id: Date.now(),
      contractServiceId: "",
      servicesRendered: "",
      invoiceDescription: "",
      billingClassificationRows: [createEmptyBillingClassificationRow(assignedCompanyNumbers)],
      companyNumber:
        assignedCompanyNumbers.length > 1 ? (assignedCompanyNumbers[0] ?? "") : "",
      billingDataClassification: "",
      productClass: "",
      productCode: "",
      productType: "",
      productLine: "",
      warehouseName: "",
      basedOn: "",
      clientRate: "",
      laborPercent: "",
      frequency: "Monthly",
      effectiveDate: (/* @__PURE__ */ new Date()).toISOString().split("T")[0],
      endDate: "",
      opCodes: [],
      laborMappingRows: [createEmptyLaborMappingRow(assignedCompanyNumbers)],
    };
    setProfiles(profiles.map(
      (p) => (p.id === profileId ? { ...p, services: [newService, ...p.services] } : p)
    ));
    openProfileServiceDrawer(profileId, newService.id);
  };
  const requestRemoveService = (profileId, serviceId) => {
    setEndServiceError("");
    setPendingRemoveService({ profileId, serviceId });
  };

  const handleCancelRemoveService = () => {
    if (isEndingInvProfileDetail) {
      return;
    }
    setPendingRemoveService(null);
    setEndServiceError("");
  };

  const removeServiceFromProfile = (profileId, serviceId) => {
    setProfiles((prev) =>
      prev.map((p) =>
        p.id === profileId ? { ...p, services: p.services.filter((s) => s.id !== serviceId) } : p
      )
    );
    if (editingServiceId === serviceId) {
      setEditingServiceId(null);
      setServiceDrawerProfileId(null);
    }
    setServiceSaveErrors((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setServiceSaveSuccess((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setPendingRemoveService(null);
  };

  const closeProfileServiceDrawer = () => {
    const profileId = serviceDrawerProfileId;
    const serviceId = editingServiceId;
    if (profileId != null && serviceId != null) {
      const profile = profiles.find((p) => p.id === profileId);
      const service = profile?.services?.find((s) => s.id === serviceId);
      if (service && !service.invProfileDetailId) {
        removeServiceFromProfile(profileId, serviceId);
      } else {
        setEditingServiceId(null);
        setServiceSaveErrors((prev) => {
          const next = { ...prev };
          delete next[serviceId];
          return next;
        });
      }
    }
    setServiceDrawerProfileId(null);
    profileServiceEditBaselineRef.current = null;
    profileServiceEditSessionRef.current = null;
    setProfileServiceEditBaselineReady(false);
  };

  const handleConfirmRemoveService = async () => {
    if (!pendingRemoveService) {
      return;
    }
    const { profileId, serviceId } = pendingRemoveService;
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    if (!service.invProfileDetailId) {
      removeServiceFromProfile(profileId, serviceId);
      return;
    }
    if (custIdForQuery == null) {
      setEndServiceError(
        "Ending a service requires an existing client. Open this page from Client Configuration."
      );
      return;
    }
    setEndServiceError("");
    try {
      const row = await endInvProfileDetail({
        custId: custIdForQuery,
        invProfileId: profile.id,
        invProfileDetailId: Number(service.invProfileDetailId),
      }).unwrap();
      const today = todayIsoDate();
      const ui = {
        ...mapInvProfileDetailRowToUi(row, getInvoiceProfileAssignedCompanyNumbers(profile)),
        id: service.id,
        frequency: service.frequency,
        endDate: formatApiDateForDisplay(row.endDate ?? row.end_date) || today,
        isEnded: true,
        endAppliedByDisplayName: loggedInEmployeeDisplayName,
      };
      setProfiles((prev) =>
        prev.map((p) => {
          if (p.id !== profileId) {
            return p;
          }
          const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
          const saved = sortSavedProfileServicesNewestFirst(
            p.services
              .filter((s) => s.invProfileDetailId)
              .map((s) => (s.id === serviceId ? ui : s))
          );
          return { ...p, services: [...unsaved, ...saved] };
        })
      );
      if (editingServiceId === serviceId) {
        setEditingServiceId(null);
        setServiceDrawerProfileId(null);
      }
      setPendingRemoveService(null);
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const first = Object.values(data.errors)[0];
        setEndServiceError(typeof first === "string" ? first : String(first));
        return;
      }
      setEndServiceError(
        data?.detail || data?.title || "Unable to end service. Please try again."
      );
    }
  };
  const clearServiceFieldError = (serviceId, fieldKey) => {
    setServiceSaveErrors((prev) => {
      const row = prev[serviceId];
      if (!row?.[fieldKey]) {
        return prev;
      }
      const nextRow = { ...row };
      delete nextRow[fieldKey];
      const next = { ...prev };
      if (Object.keys(nextRow).length === 0) {
        delete next[serviceId];
      } else {
        next[serviceId] = nextRow;
      }
      return next;
    });
  };
  const applyEffectiveDateValidation = (serviceId, contractServiceId, effectiveDate) => {
    const startDate = getContractServiceStartDate(
      contractServiceId,
      contractServiceInvoiceDescriptionOptions
    );
    const message = validateEffectiveDateAgainstContractServiceStart(effectiveDate, startDate);
    if (message) {
      setServiceSaveErrors((prev) => ({
        ...prev,
        [serviceId]: { ...(prev[serviceId] ?? {}), effectiveDate: message },
      }));
    } else {
      clearServiceFieldError(serviceId, "effectiveDate");
    }
  };
  const updateServiceField = (profileId, serviceId, field, value) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    setProfiles(profiles.map(
      (p) => p.id === profileId ? {
        ...p,
        services: p.services.map((s) => {
          if (s.id !== serviceId) {
            return s;
          }
          if (field === "servicesRendered") {
            const servicesRendered = String(value ?? "").trim();
            const clientRateFromOptions = getContractRateForServicesRendered(
              servicesRendered,
              contractServiceInvoiceDescriptionOptions
            );
            const invoiceDescription = getInvoiceDescriptionForServicesRendered(
              servicesRendered,
              contractServiceInvoiceDescriptionOptions,
              s.invoiceDescription
            );
            const matches = contractServiceInvoiceDescriptionOptions.filter(
              (opt) => String(opt.servicesRendered ?? "").trim() === servicesRendered
            );
            const contractServiceId =
              matches.length === 1 ? String(matches[0].contractServiceId ?? "") : s.contractServiceId;
            const next = {
              ...s,
              servicesRendered,
              basedOn: getBasedOnForServicesRendered(
                servicesRendered,
                contractServiceInvoiceDescriptionOptions
              ),
              clientRate: clientRateFromOptions,
              invoiceDescription,
              contractServiceId,
            };
            if (
              !isLaborCategory(
                getCategoryForProfileService(next, contractServiceInvoiceDescriptionOptions)
              )
            ) {
              next.opCodes = [];
            }
            if (!isLaborHourService(next, contractServiceInvoiceDescriptionOptions)) {
              next.laborMappingRows = [];
            } else {
              const assigned = getInvoiceProfileAssignedCompanyNumbers(p);
              next.laborMappingRows = ensureLaborMappingRows(next, assigned);
            }
            if (!isLaborPercentService(next, contractServiceInvoiceDescriptionOptions)) {
              next.laborPercent = "";
            }
            if (profileServiceOmitsBillingClassification(next, contractServiceInvoiceDescriptionOptions)) {
              next.billingClassificationRows = [];
            }
            return next;
          }
          if (field === "invoiceDescription") {
            const next = { ...s, [field]: value };
            if (
              !isLaborCategory(
                getCategoryForProfileService(next, contractServiceInvoiceDescriptionOptions)
              )
            ) {
              next.opCodes = [];
            }
            if (!isLaborPercentService(next, contractServiceInvoiceDescriptionOptions)) {
              next.laborPercent = "";
            }
            if (profileServiceOmitsBillingClassification(next, contractServiceInvoiceDescriptionOptions)) {
              next.billingClassificationRows = [];
            }
            return next;
          }
          if (field === "opCodes") {
            const selected = Array.isArray(value) ? value : [];
            return { ...s, opCodes: mergeSelectedOpCodes(s.opCodes, selected) };
          }
          if (field === "billingDataClassification") {
            return {
              ...s,
              billingDataClassification: value,
              productClass: "",
              productCode: "",
              productType: "",
              productLine: "",
              warehouseName: "",
            };
          }
          return { ...s, [field]: value };
        }),
      } : p
    ));
    if (
      field === "billingDataClassification" ||
      field === "productClass" ||
      field === "productCode" ||
      field === "productType" ||
      field === "productLine" ||
      field === "warehouseName"
    ) {
      clearServiceFieldError(serviceId, field);
    }
    if (field === "billingDataClassification") {
      clearServiceFieldError(serviceId, "productDivision");
      clearServiceFieldError(serviceId, "productClass");
      clearServiceFieldError(serviceId, "productCode");
      clearServiceFieldError(serviceId, "productType");
      clearServiceFieldError(serviceId, "productLine");
      clearServiceFieldError(serviceId, "warehouseName");
    }
    if (field === "frequency") {
      clearServiceFieldError(serviceId, field);
    }
    if (field === "servicesRendered") {
      if (String(value ?? "").trim()) {
        clearServiceFieldError(serviceId, "servicesRendered");
        const contractRate = getContractRateForServicesRendered(
          value,
          contractServiceInvoiceDescriptionOptions
        );
        if (hasContractRateValue(contractRate)) {
          clearServiceFieldError(serviceId, "clientRate");
        }
      }
    }
    if (field === "invoiceDescription") {
      clearServiceFieldError(serviceId, "invoiceDescription");
    }
    if (field === "clientRate") {
      clearServiceFieldError(serviceId, "clientRate");
    }
    if (field === "laborPercent") {
      clearServiceFieldError(serviceId, "laborPercent");
    }
    if (field === "effectiveDate") {
      const resolvedId = resolveContractServiceIdForService(
        { ...service, [field]: value },
        contractServiceInvoiceDescriptionOptions
      );
      applyEffectiveDateValidation(serviceId, resolvedId, value);
    }
    if (field === "servicesRendered" || field === "invoiceDescription") {
      const updated =
        field === "servicesRendered"
          ? {
              ...service,
              servicesRendered: String(value ?? "").trim(),
              basedOn: getBasedOnForServicesRendered(
                value,
                contractServiceInvoiceDescriptionOptions
              ),
              clientRate: getContractRateForServicesRendered(
                value,
                contractServiceInvoiceDescriptionOptions
              ),
              invoiceDescription: getInvoiceDescriptionForServicesRendered(
                value,
                contractServiceInvoiceDescriptionOptions,
                service.invoiceDescription
              ),
            }
          : { ...service, [field]: value };
      if (updated.effectiveDate) {
        const resolvedId = resolveContractServiceIdForService(
          updated,
          contractServiceInvoiceDescriptionOptions
        );
        applyEffectiveDateValidation(serviceId, resolvedId, updated.effectiveDate);
      }
    }
  };

  const clearServiceBillingClassificationErrors = (serviceId) => {
    setServiceSaveErrors((prev) => {
      const errs = prev[serviceId];
      if (!errs) {
        return prev;
      }
      const next = { ...prev };
      const copy = { ...errs };
      delete copy.billingClassificationRows;
      delete copy.billingDataClassification;
      delete copy.companyNumber;
      delete copy.productDivision;
      delete copy.productClass;
      delete copy.productCode;
      delete copy.productType;
      delete copy.productLine;
      delete copy.laborMappingRows;
      if (Object.keys(copy).length === 0) {
        delete next[serviceId];
      } else {
        next[serviceId] = copy;
      }
      return next;
    });
  };

  const patchServiceBillingClassificationRow = (profileId, serviceId, rowId, patch) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureBillingClassificationRows(service, assigned);
    const nextRows = rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r));
    const rowErrorsList = validateBillingClassificationRows(
      nextRows,
      assigned,
      billingLookupValidationByRowRef.current,
      productDivisionsByRowRef.current
    );

    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        return {
          ...p,
          services: (p.services ?? []).map((s) =>
            s.id === serviceId
              ? syncLegacyBillingFieldsFromRows({
                  ...s,
                  billingClassificationRows: nextRows,
                })
              : s
          ),
        };
      })
    );

    if (rowErrorsList) {
      setServiceSaveErrors((prev) => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], billingClassificationRows: rowErrorsList },
      }));
    } else {
      clearServiceBillingClassificationErrors(serviceId);
    }
  };

  const addBillingClassificationRowAfterIndex = (profileId, serviceId, rowIndex) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureBillingClassificationRows(service, assigned);
    if (rowIndex < 0 || rowIndex >= rows.length || rowIndex !== rows.length - 1) {
      return;
    }
    const row = rows[rowIndex];
    const lookup = billingLookupValidationByRowRef.current[row.id] ?? {};
    const rowErrorsList = validateBillingClassificationRows(
      rows,
      assigned,
      billingLookupValidationByRowRef.current,
      productDivisionsByRowRef.current
    );
    if (rowErrorsList?.[rowIndex]) {
      setServiceSaveErrors((prev) => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], billingClassificationRows: rowErrorsList },
      }));
      return;
    }
    if (!isBillingClassificationRowComplete(row, lookup, assigned)) {
      const rowErr =
        validateSingleBillingClassificationRowErrors(row, lookup, assigned) ?? {
          billingDataClassification: "Complete this classification before adding another.",
        };
      setServiceSaveErrors((prev) => {
        const prior = Array.isArray(prev[serviceId]?.billingClassificationRows)
          ? [...prev[serviceId].billingClassificationRows]
          : rows.map(() => null);
        const nextRowErrs = rows.map((_, i) => (i === rowIndex ? rowErr : prior[i] ?? null));
        return {
          ...prev,
          [serviceId]: { ...prev[serviceId], billingClassificationRows: nextRowErrs },
        };
      });
      return;
    }
    const nextRows = [...rows];
    nextRows.splice(rowIndex + 1, 0, createEmptyBillingClassificationRow(assigned));
    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        return {
          ...p,
          services: (p.services ?? []).map((s) =>
            s.id === serviceId
              ? syncLegacyBillingFieldsFromRows({
                  ...s,
                  billingClassificationRows: nextRows,
                })
              : s
          ),
        };
      })
    );
    clearServiceBillingClassificationErrors(serviceId);
  };

  const clearServiceLaborMappingErrors = (serviceId) => {
    setServiceSaveErrors((prev) => {
      const errs = prev[serviceId];
      if (!errs) {
        return prev;
      }
      const next = { ...prev };
      const copy = { ...errs };
      delete copy.laborMappingRows;
      if (Object.keys(copy).length === 0) {
        delete next[serviceId];
      } else {
        next[serviceId] = copy;
      }
      return next;
    });
  };

  const patchServiceLaborMappingRow = (profileId, serviceId, rowId, patch) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureLaborMappingRows(service, assigned);
    const nextRows = rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r));
    const rowErrorsList = validateLaborMappingRows(nextRows, assigned);

    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        return {
          ...p,
          services: (p.services ?? []).map((s) =>
            s.id === serviceId ? { ...s, laborMappingRows: nextRows } : s
          ),
        };
      })
    );

    if (rowErrorsList) {
      setServiceSaveErrors((prev) => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], laborMappingRows: rowErrorsList },
      }));
    } else {
      clearServiceLaborMappingErrors(serviceId);
    }
  };

  const addLaborMappingRowAfterIndex = (profileId, serviceId, rowIndex) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
    const rows = ensureLaborMappingRows(service, assigned);
    if (rowIndex < 0 || rowIndex >= rows.length || rowIndex !== rows.length - 1) {
      return;
    }
    const row = rows[rowIndex];
    const rowErrorsList = validateLaborMappingRows(rows, assigned);
    if (rowErrorsList?.[rowIndex]) {
      setServiceSaveErrors((prev) => {
        const prior = Array.isArray(prev[serviceId]?.laborMappingRows)
          ? [...prev[serviceId].laborMappingRows]
          : rows.map(() => null);
        const nextRowErrs = rows.map((_, i) => rowErrorsList[i] ?? prior[i] ?? null);
        return {
          ...prev,
          [serviceId]: { ...prev[serviceId], laborMappingRows: nextRowErrs },
        };
      });
      return;
    }
    if (!isLaborMappingRowComplete(row, assigned)) {
      const rowErr =
        validateSingleLaborMappingRowErrors(row, assigned) ?? {
          jobNo: "Complete this labor mapping before adding another.",
        };
      setServiceSaveErrors((prev) => {
        const prior = Array.isArray(prev[serviceId]?.laborMappingRows)
          ? [...prev[serviceId].laborMappingRows]
          : rows.map(() => null);
        const nextRowErrs = rows.map((_, i) => (i === rowIndex ? rowErr : prior[i] ?? null));
        return {
          ...prev,
          [serviceId]: { ...prev[serviceId], laborMappingRows: nextRowErrs },
        };
      });
      return;
    }
    const nextRows = [...rows];
    nextRows.splice(rowIndex + 1, 0, createEmptyLaborMappingRow(assigned));
    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        return {
          ...p,
          services: (p.services ?? []).map((s) =>
            s.id === serviceId ? { ...s, laborMappingRows: nextRows } : s
          ),
        };
      })
    );
    clearServiceLaborMappingErrors(serviceId);
  };

  const removeLastLaborMappingRow = (profileId, serviceId) => {
    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        const assigned = getInvoiceProfileAssignedCompanyNumbers(p);
        return {
          ...p,
          services: (p.services ?? []).map((s) => {
            if (s.id !== serviceId) {
              return s;
            }
            const rows = ensureLaborMappingRows(s, assigned);
            if (rows.length <= 1) {
              return s;
            }
            return { ...s, laborMappingRows: rows.slice(0, -1) };
          }),
        };
      })
    );
    clearServiceLaborMappingErrors(serviceId);
  };

  const removeLastBillingClassificationRow = (profileId, serviceId) => {
    setProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== profileId) {
          return p;
        }
        const assigned = getInvoiceProfileAssignedCompanyNumbers(p);
        return {
          ...p,
          services: (p.services ?? []).map((s) => {
            if (s.id !== serviceId) {
              return s;
            }
            const rows = ensureBillingClassificationRows(s, assigned);
            if (rows.length <= 1) {
              return s;
            }
            const nextRows = rows.slice(0, -1);
            return syncLegacyBillingFieldsFromRows({
              ...s,
              billingClassificationRows: nextRows,
            });
          }),
        };
      })
    );
    clearServiceBillingClassificationErrors(serviceId);
  };

  const saveServiceToProfile = async (profileId, serviceId) => {
    const profile = profiles.find((p) => p.id === profileId);
    const service = profile?.services?.find((s) => s.id === serviceId);
    if (!profile || !service) {
      return;
    }
    if (serviceHasEndDate(service)) {
      setEditingServiceId(null);
      return;
    }
    const isUpdate = Boolean(service.invProfileDetailId);
    const localErrors = {};
    if (!String(service.servicesRendered ?? "").trim()) {
      localErrors.servicesRendered = "Services rendered is required.";
    }
    if (!String(service.invoiceDescription ?? "").trim()) {
      localErrors.invoiceDescription = "Invoice description is required.";
    }
    const resolvedContractServiceId = resolveContractServiceIdForService(
      service,
      contractServiceInvoiceDescriptionOptions
    );
    if (!resolvedContractServiceId) {
      localErrors._contractServiceLink =
        "No matching contract service for this services rendered value. If several apply, enter an invoice description that exactly matches a contract service description.";
    }
    if (!String(service.frequency ?? "").trim()) {
      localErrors.frequency = "Frequency is required.";
    }
    if (!String(service.effectiveDate ?? "").trim()) {
      localErrors.effectiveDate = "Effective date is required.";
    } else {
      const serviceStartDate = getContractServiceStartDate(
        resolvedContractServiceId,
        contractServiceInvoiceDescriptionOptions
      );
      const effectiveDateError = validateEffectiveDateAgainstContractServiceStart(
        service.effectiveDate,
        serviceStartDate
      );
      if (effectiveDateError) {
        localErrors.effectiveDate = effectiveDateError;
      }
    }
    if (!hasContractRateValue(service.clientRate)) {
      localErrors.clientRate = "Client rate is required.";
    }
    if (isLaborPercentService(service, contractServiceInvoiceDescriptionOptions)) {
      const percentError = validateLaborPercentValue(service.laborPercent);
      if (percentError) {
        localErrors.laborPercent = percentError;
      }
    }
    if (isLaborHourService(service, contractServiceInvoiceDescriptionOptions)) {
      const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
      const rows = ensureLaborMappingRows(service, assigned);
      const rowErrorsList = validateLaborMappingRows(rows, assigned);
      if (rowErrorsList) {
        localErrors.laborMappingRows = rowErrorsList;
      }
    } else if (
      invoiceProfileHasAssignedCompanyNumber(profile) &&
      !isFlatFeeCategory(
        getCategoryForProfileService(service, contractServiceInvoiceDescriptionOptions)
      )
    ) {
      const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
      const rows = ensureBillingClassificationRows(service, assigned);
      const rowErrorsList = validateBillingClassificationRows(
        rows,
        assigned,
        billingLookupValidationByRowRef.current,
        productDivisionsByRowRef.current
      );
      if (rowErrorsList) {
        localErrors.billingClassificationRows = rowErrorsList;
      }
    }
    if (Object.keys(localErrors).length > 0) {
      setServiceSaveErrors((prev) => ({ ...prev, [serviceId]: localErrors }));
      return;
    }
    if (custIdForQuery == null) {
      setServiceSaveErrors((prev) => ({
        ...prev,
        [serviceId]: {
          _global: "Saving requires an existing client. Open this page from Client Configuration.",
        },
      }));
      return;
    }
    setServiceSaveErrors((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setServiceSaveSuccess((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    try {
      const body = buildProfileServiceSaveBody(
        service,
        profile,
        contractServiceInvoiceDescriptionOptions,
        productDivisionsByRowRef.current,
        isUpdate
          ? service.sequenceDisplay ?? null
          : nextProfileServiceSequenceDisplay(profile.services)
      );
      const row = isUpdate
        ? await updateInvProfileDetail({
            custId: custIdForQuery,
            invProfileId: profile.id,
            invProfileDetailId: Number(service.invProfileDetailId),
            body,
          }).unwrap()
        : await createInvProfileDetail({
            custId: custIdForQuery,
            invProfileId: profile.id,
            body,
          }).unwrap();
      const mapped = mapInvProfileDetailRowToUi(
        row,
        getInvoiceProfileAssignedCompanyNumbers(profile)
      );
      const savedLaborMappingRows = laborMappingRowsFromOpCodes(
        row.opCodes ?? row.op_codes ?? service.opCodes,
        getInvoiceProfileAssignedCompanyNumbers(profile)
      );
      const ui = {
        ...mapped,
        id: service.id,
        frequency: service.frequency,
        opCodes: normalizeOpCodesFromApi(row.opCodes ?? row.op_codes ?? service.opCodes),
        laborMappingRows: savedLaborMappingRows,
      };
      setProfiles((prev) =>
        prev.map((p) => {
          if (p.id !== profileId) {
            return p;
          }
          if (isUpdate) {
            const unsaved = (p.services ?? []).filter((s) => !s.invProfileDetailId);
            const saved = sortSavedProfileServicesNewestFirst(
              p.services
                .filter((s) => s.invProfileDetailId)
                .map((s) => (s.id === serviceId ? ui : s))
            );
            return { ...p, services: [...unsaved, ...saved] };
          }
          return {
            ...p,
            services: [
              ui,
              ...sortSavedProfileServicesNewestFirst(
                p.services.filter((s) => s.id !== serviceId && s.invProfileDetailId)
              ),
            ],
          };
        })
      );
      setEditingServiceId(null);
      setServiceDrawerProfileId(null);
      profileServiceEditBaselineRef.current = null;
      profileServiceEditSessionRef.current = null;
      setProfileServiceEditBaselineReady(false);
      setServiceSaveSuccess((prev) => {
        const next = { ...prev };
        delete next[serviceId];
        next[ui.id] = true;
        return next;
      });
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const mapped = {};
        for (const [k, v] of Object.entries(data.errors)) {
          const key = k === "defaultInvoiceDescription" ? "invoiceDescription" : k;
          mapped[key] = typeof v === "string" ? v : String(v);
        }
        setServiceSaveErrors((prev) => ({ ...prev, [serviceId]: mapped }));
        return;
      }
      const message = data?.detail || data?.title || "Unable to save service.";
      setServiceSaveErrors((prev) => ({ ...prev, [serviceId]: { _global: message } }));
    }
  };
  const handleServicesRenderedSelect = (profileId, serviceId, servicesRenderedRaw) => {
    updateServiceField(profileId, serviceId, "servicesRendered", String(servicesRenderedRaw ?? "").trim());
  };
  const resolvedClient = useMemo(
    () => clients.find((c) => c.code === selectedClient),
    [clients, selectedClient]
  );
  const clientDisplayName = useMemo(() => {
    const nameFromUrl = (clientNameFromUrl || "").trim();
    const codeFromUrl = (clientCodeFromUrl || "").trim();
    const selectedMatchesUrl =
      !!resolvedClient &&
      ((codeFromUrl &&
        String(resolvedClient.code ?? "").toUpperCase() === codeFromUrl.toUpperCase()) ||
        (nameFromUrl &&
          (resolvedClient.name || "").trim().toLowerCase() === nameFromUrl.toLowerCase()));
    if (resolvedClient?.name?.trim() && selectedMatchesUrl) {
      return resolvedClient.name.trim();
    }
    if (nameFromUrl) {
      return nameFromUrl;
    }
    if (resolvedClient?.name?.trim()) {
      return resolvedClient.name.trim();
    }
    if (codeFromUrl) {
      return codeFromUrl;
    }
    if (clients.length > 0) {
      return (clients[0].name || clients[0].code || "").trim() || "—";
    }
    return "—";
  }, [resolvedClient, clientNameFromUrl, clientCodeFromUrl, clients]);
  const invoiceProfilePageTitle = useMemo(
    () => formatInvoiceProfileConfigurationTitle(clientDisplayName),
    [clientDisplayName]
  );
  useEffect(() => {
    if (embedded) {
      return;
    }
    const prev = document.title;
    document.title = invoiceProfilePageTitle;
    return () => {
      document.title = prev;
    };
  }, [embedded, invoiceProfilePageTitle]);

  const focusedProfileLabel = useMemo(() => {
    if (focusProfileIdNormalized == null) {
      return "";
    }
    const p = profiles.find(
      (row) => normalizeProfileId(row.id) === focusProfileIdNormalized
    );
    if (!p) {
      return "";
    }
    const program = String(p.invoiceNameProgram ?? "").trim();
    if (program) {
      return program;
    }
    const company = String(p.companyNumber ?? "").trim();
    return company || "Invoice profile";
  }, [profiles, focusProfileIdNormalized]);

  const drawerProfile =
    serviceDrawerProfileId != null
      ? profiles.find((p) => profileIdsMatch(p.id, serviceDrawerProfileId))
      : null;
  const drawerService =
    drawerProfile && editingServiceId != null
      ? drawerProfile.services?.find((s) => serviceIdsMatch(s.id, editingServiceId))
      : null;
  const showProfileServiceDrawer = Boolean(drawerService);
  const drawerRowErrors =
    editingServiceId != null ? serviceSaveErrors[editingServiceId] ?? {} : {};
  const drawerInvoiceDescChoices = drawerService
    ? getInvoiceDescriptionChoicesForServicesRendered(
        drawerService.servicesRendered,
        contractServiceInvoiceDescriptionOptions
      )
    : [];
  const drawerServiceCategory = drawerService
    ? getCategoryForProfileService(drawerService, contractServiceInvoiceDescriptionOptions)
    : "";
  const drawerIsLaborHour = drawerService
    ? isLaborHourService(drawerService, contractServiceInvoiceDescriptionOptions)
    : false;
  const drawerIsLaborPercent = drawerService
    ? isLaborPercentService(drawerService, contractServiceInvoiceDescriptionOptions)
    : false;
  const drawerIsFlatFee = isFlatFeeCategory(drawerServiceCategory);
  const drawerServicesRenderedSelected = Boolean(
    String(drawerService?.servicesRendered ?? "").trim()
  );
  const showAddOpCodesDropdown = isLaborCategory(drawerServiceCategory);
  const showPercentageField = drawerServicesRenderedSelected && drawerIsLaborPercent;
  const showBillingDataClassificationDropdown =
    drawerServicesRenderedSelected &&
    invoiceProfileHasAssignedCompanyNumber(drawerProfile) &&
    !drawerIsLaborHour &&
    !drawerIsFlatFee;
  const showLaborMappingDropdown = drawerServicesRenderedSelected && drawerIsLaborHour;
  const drawerAssignedCompanyNumbers = drawerProfile
    ? getInvoiceProfileAssignedCompanyNumbers(drawerProfile)
    : [];
  const drawerBillingRows =
    drawerProfile && drawerService
      ? ensureBillingClassificationRows(drawerService, drawerAssignedCompanyNumbers)
      : [];
  const drawerBillingRowErrors = Array.isArray(drawerRowErrors.billingClassificationRows)
    ? drawerRowErrors.billingClassificationRows
    : [];
  const {
    data: drawerProfileJobNos = [],
    isLoading: isDrawerProfileJobNosLoading,
    isError: isDrawerProfileJobNosError,
  } = useGetClientConfigurationCustomerInvProfileJobnosQuery(
    { custId: custIdForQuery, invProfileId: serviceDrawerProfileId },
    { skip: custIdForQuery == null || serviceDrawerProfileId == null }
  );
  const drawerLaborMappingRows =
    drawerProfile && drawerService
      ? ensureLaborMappingRows(drawerService, drawerAssignedCompanyNumbers)
      : [];
  const drawerLaborMappingRowErrors = Array.isArray(drawerRowErrors.laborMappingRows)
    ? drawerRowErrors.laborMappingRows
    : [];
  const handleBillingRowLookupStateChange = useCallback((rowId, lookup) => {
    billingLookupValidationByRowRef.current = {
      ...billingLookupValidationByRowRef.current,
      [rowId]: lookup,
    };
    setBillingLookupRevision((n) => n + 1);
  }, []);
  const handleBillingRowProductDivisionsChange = useCallback((rowId, divisions) => {
    productDivisionsByRowRef.current = {
      ...productDivisionsByRowRef.current,
      [rowId]: divisions,
    };
    setProductDivisionsRevision((n) => n + 1);
  }, []);
  const billingLookupByRowId = useMemo(() => {
    void billingLookupRevision;
    return { ...billingLookupValidationByRowRef.current };
  }, [billingLookupRevision]);
  const productDivisionsByRowId = useMemo(() => {
    void productDivisionsRevision;
    return { ...productDivisionsByRowRef.current };
  }, [productDivisionsRevision]);
  // Re-run classification validation when lookups finish so stale "No Data Found"
  // errors clear once dropdown options are available.
  useEffect(() => {
    if (serviceDrawerProfileId == null || editingServiceId == null) {
      return;
    }
    const profile = profiles.find((p) => p.id === serviceDrawerProfileId);
    const service = profile?.services?.find((s) => s.id === editingServiceId);
    if (!profile || !service) {
      return;
    }
    if (
      profileServiceOmitsBillingClassification(service, contractServiceInvoiceDescriptionOptions)
    ) {
      return;
    }
    setServiceSaveErrors((prev) => {
      const existing = prev[editingServiceId]?.billingClassificationRows;
      if (existing == null) {
        return prev;
      }
      const assigned = getInvoiceProfileAssignedCompanyNumbers(profile);
      const rows = ensureBillingClassificationRows(service, assigned);
      const rowErrorsList = validateBillingClassificationRows(
        rows,
        assigned,
        billingLookupValidationByRowRef.current,
        productDivisionsByRowRef.current
      );
      if (!rowErrorsList) {
        const errs = prev[editingServiceId];
        if (!errs) {
          return prev;
        }
        const next = { ...prev };
        const copy = { ...errs };
        delete copy.billingClassificationRows;
        delete copy.billingDataClassification;
        delete copy.companyNumber;
        delete copy.productDivision;
        delete copy.productClass;
        delete copy.productCode;
        delete copy.productType;
        delete copy.productLine;
        if (Object.keys(copy).length === 0) {
          delete next[editingServiceId];
        } else {
          next[editingServiceId] = copy;
        }
        return next;
      }
      if (JSON.stringify(existing) === JSON.stringify(rowErrorsList)) {
        return prev;
      }
      return {
        ...prev,
        [editingServiceId]: {
          ...prev[editingServiceId],
          billingClassificationRows: rowErrorsList,
        },
      };
    });
  }, [
    billingLookupRevision,
    productDivisionsRevision,
    serviceDrawerProfileId,
    editingServiceId,
    profiles,
    contractServiceInvoiceDescriptionOptions,
  ]);
  useLayoutEffect(() => {
    if (!showProfileServiceDrawer || !drawerService?.invProfileDetailId || !drawerProfile) {
      profileServiceEditBaselineRef.current = null;
      profileServiceEditSessionRef.current = null;
      setProfileServiceEditBaselineReady(false);
      return;
    }
    if (isInvoiceDescriptionOptionsLoading) {
      setProfileServiceEditBaselineReady(false);
      return;
    }
    const sessionKey = `${serviceDrawerProfileId}:${editingServiceId}`;
    if (profileServiceEditSessionRef.current !== sessionKey) {
      profileServiceEditSessionRef.current = sessionKey;
      profileServiceEditBaselineRef.current = JSON.stringify(
        buildProfileServiceFormSaveComparable(
          drawerService,
          drawerProfile,
          contractServiceInvoiceDescriptionOptions,
          productDivisionsByRowRef.current
        )
      );
    }
    setProfileServiceEditBaselineReady(true);
  }, [
    showProfileServiceDrawer,
    drawerService,
    drawerProfile,
    editingServiceId,
    serviceDrawerProfileId,
    contractServiceInvoiceDescriptionOptions,
    isInvoiceDescriptionOptionsLoading,
  ]);
  const hasProfileServiceFormChanges = useMemo(() => {
    if (editingServiceId == null || !drawerService?.invProfileDetailId) {
      return true;
    }
    if (!profileServiceEditBaselineReady) {
      return false;
    }
    const baseline = profileServiceEditBaselineRef.current;
    if (!baseline || !drawerProfile) {
      return false;
    }
    return (
      JSON.stringify(
        buildProfileServiceFormSaveComparable(
          drawerService,
          drawerProfile,
          contractServiceInvoiceDescriptionOptions,
          productDivisionsByRowId
        )
      ) !== baseline
    );
  }, [
    drawerService,
    drawerProfile,
    contractServiceInvoiceDescriptionOptions,
    editingServiceId,
    productDivisionsByRowId,
    profileServiceEditBaselineReady,
  ]);
  const drawerSelectedOpCodes = drawerService
    ? selectedOpCodeStrings(normalizeServiceOpCodes(drawerService))
    : [];
  const opCodesMultiselectSize = Math.min(8, Math.max(4, operationOptions.length));

  const createProfileModalTitle =
    embedded || createModalOnly ? "Add Invoice Profile" : "Create Profile";
  const createProfileSubmitLabel =
    embedded || createModalOnly ? "Add Invoice Profile" : "Create";
  const canSubmitCreateProfile = useMemo(
    () =>
      isCreateProfileFormComplete({
        client: newProfileClient,
        invoiceNameProgram: newProfileInvoiceNameProgram,
        companyNumbers: newProfileCompanyNumbers,
        jackets: newProfileJackets,
        custId: custIdForQuery,
      }),
    [
      newProfileClient,
      newProfileInvoiceNameProgram,
      newProfileCompanyNumbers,
      newProfileJackets,
      custIdForQuery,
    ]
  );

  const createProfileModal = showCreateModal ? (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={closeCreateProfileModal}
        role="presentation"
      />

      <div className="fixed inset-y-0 right-0 flex w-full max-w-2xl">
        <div className="relative flex w-full flex-col bg-white shadow-xl dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {createProfileModalTitle}
            </h3>
            <button
              type="button"
              onClick={closeCreateProfileModal}
              className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {createProfileErrors._global ? (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {createProfileErrors._global}
                </p>
              ) : null}

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Client *
                </label>
                <div
                  ref={createModalClientFocusRef}
                  tabIndex={-1}
                  className={`w-full cursor-not-allowed rounded border px-4 py-2 bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100 ${
                    createProfileErrors.client
                      ? "border-red-500 dark:border-red-500 ring-1 ring-red-500/40"
                      : "border-gray-300 dark:border-gray-700"
                  }`}
                  aria-invalid={createProfileErrors.client ? "true" : "false"}
                  aria-describedby={createProfileErrors.client ? "create-profile-error-client" : undefined}
                >
                  {clientDisplayName}
                </div>
                {createProfileErrors.client ? (
                  <p id="create-profile-error-client" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                    {createProfileErrors.client}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="create-profile-invoice-name-program" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Invoice Name/Program *
                </label>
                <input
                  id="create-profile-invoice-name-program"
                  ref={createModalInvoiceNameProgramRef}
                  type="text"
                  value={newProfileInvoiceNameProgram}
                  onChange={(e) => {
                    setNewProfileInvoiceNameProgram(e.target.value);
                    setCreateProfileErrors((prev) => {
                      if (!prev.invoiceNameProgram) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.invoiceNameProgram;
                      return next;
                    });
                  }}
                  aria-invalid={createProfileErrors.invoiceNameProgram ? "true" : "false"}
                  aria-describedby={
                    createProfileErrors.invoiceNameProgram ? "create-profile-error-invoice-name-program" : undefined
                  }
                  className={serviceDrawerFieldClassName(Boolean(createProfileErrors.invoiceNameProgram))}
                  placeholder="Enter invoice name or program"
                />
                {createProfileErrors.invoiceNameProgram ? (
                  <p
                    id="create-profile-error-invoice-name-program"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {createProfileErrors.invoiceNameProgram}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="create-profile-company-number" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company # *
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Select one or more company numbers (Ctrl+click or Cmd+click for multiple). N/A cannot be combined with other values.
                </p>
                <select
                  id="create-profile-company-number"
                  ref={createModalCompanyNumberRef}
                  multiple
                  size={companyMultiselectSize}
                  value={newProfileCompanyNumbers}
                  disabled={isCompanyNumbersLoading}
                  onChange={(e) => {
                    let selected = Array.from(e.target.selectedOptions, (option) => option.value);
                    if (
                      selected.includes(CREATE_PROFILE_NOT_APPLICABLE) &&
                      selected.length > 1
                    ) {
                      selected = selected.filter((v) => v !== CREATE_PROFILE_NOT_APPLICABLE);
                    }
                    setNewProfileCompanyNumbers(selected);
                    setCreateProfileErrors((prev) => {
                      if (!prev.companyNumber) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.companyNumber;
                      return next;
                    });
                  }}
                  aria-invalid={createProfileErrors.companyNumber ? "true" : "false"}
                  aria-describedby={[
                    createProfileErrors.companyNumber ? "create-profile-error-company-number" : "",
                    isCompanyNumbersError ? "create-profile-error-company-number-load" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                  className={`${serviceDrawerFieldClassName(Boolean(createProfileErrors.companyNumber))} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {companySelectOptions.map((no) => (
                    <option key={no} value={no}>
                      {no}
                    </option>
                  ))}
                </select>
                {isCompanyNumbersError ? (
                  <p
                    id="create-profile-error-company-number-load"
                    className="mt-1 text-xs text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    Unable to load company numbers from the database.
                  </p>
                ) : null}
                {createProfileErrors.companyNumber ? (
                  <p
                    id="create-profile-error-company-number"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {createProfileErrors.companyNumber}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="create-profile-jacket" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Jacket *
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Select one or more jackets (Ctrl+click or Cmd+click for multiple).
                </p>
                <select
                  id="create-profile-jacket"
                  ref={createModalJacketRef}
                  multiple
                  size={jacketMultiselectSize}
                  value={newProfileJackets}
                  disabled={isJacketJobsForProfileLoading || jacketJobNoOptionsForCreateProfile.length === 0}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                    setNewProfileJackets(selected);
                    setCreateProfileErrors((prev) => {
                      if (!prev.jacket && !prev.jobNo && !prev.jobNos) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.jacket;
                      delete next.jobNo;
                      delete next.jobNos;
                      return next;
                    });
                  }}
                  aria-invalid={
                    createProfileErrors.jacket || createProfileErrors.jobNo || createProfileErrors.jobNos
                      ? "true"
                      : "false"
                  }
                  aria-describedby={[
                    createProfileErrors.jacket || createProfileErrors.jobNo || createProfileErrors.jobNos
                      ? "create-profile-error-jacket"
                      : "",
                    isJacketJobsForProfileError ? "create-profile-error-jacket-load" : "",
                    jacketJobNoOptionsForCreateProfile.length === 0 && !isJacketJobsForProfileLoading
                      ? "create-profile-error-jacket-empty"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                  className={`${serviceDrawerFieldClassName(
                    Boolean(
                      createProfileErrors.jacket ||
                        createProfileErrors.jobNo ||
                        createProfileErrors.jobNos
                    )
                  )} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {jacketJobNoOptionsForCreateProfile.map((jobNo) => (
                    <option key={jobNo} value={jobNo}>
                      {jobNo}
                    </option>
                  ))}
                </select>
                {jacketJobNoOptionsForCreateProfile.length === 0 && !isJacketJobsForProfileLoading ? (
                  <p
                    id="create-profile-error-jacket-empty"
                    className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                  >
                    No jackets available for this client.
                  </p>
                ) : null}
                {isJacketJobsForProfileError ? (
                  <p
                    id="create-profile-error-jacket-load"
                    className="mt-1 text-xs text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    Unable to load jacket jobs from the database.
                  </p>
                ) : null}
                {createProfileErrors.jacket || createProfileErrors.jobNo || createProfileErrors.jobNos ? (
                  <p id="create-profile-error-jacket" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                    {createProfileErrors.jacket || createProfileErrors.jobNo || createProfileErrors.jobNos}
                  </p>
                ) : null}
                {newProfileJackets.length > 0 ? (
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    Selected: {newProfileJackets.join(", ")}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="create-profile-po-number" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  PO Number
                </label>
                <select
                  id="create-profile-po-number"
                  value={newProfilePoId}
                  disabled={isPoDropdownLoading}
                  onChange={(e) => {
                    setNewProfilePoId(e.target.value);
                    setCreateProfileErrors((prev) => {
                      if (!prev.poId) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.poId;
                      return next;
                    });
                  }}
                  aria-invalid={createProfileErrors.poId ? "true" : "false"}
                  aria-describedby={[
                    createProfileErrors.poId ? "create-profile-error-po-id" : "",
                    isPoDropdownError ? "create-profile-error-po-id-load" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                  className={`${serviceDrawerFieldClassName(Boolean(createProfileErrors.poId))} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <option value="">
                    {isPoDropdownLoading ? "Loading PO numbers…" : "Select PO (optional)"}
                  </option>
                  {poDropdownOptions.map((row) => (
                    <option key={row.poId} value={row.poId}>
                      {row.clientPo}
                    </option>
                  ))}
                </select>
                {isPoDropdownError ? (
                  <p
                    id="create-profile-error-po-id-load"
                    className="mt-1 text-xs text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    Unable to load PO numbers from the database.
                  </p>
                ) : null}
                {createProfileErrors.poId ? (
                  <p id="create-profile-error-po-id" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                    {createProfileErrors.poId}
                  </p>
                ) : null}
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                <p className="text-xs text-blue-900 dark:text-blue-100">
                  Invoice profiles group billing by company number and jacket. After creating a profile,
                  add invoice lines to link contract services and rates.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <button
              type="button"
              className="app-btn-gloss-cancel px-6"
              onClick={closeCreateProfileModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="app-btn-gloss px-6 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={createProfile}
              disabled={isCreateProfileSaving || !canSubmitCreateProfile}
              title={
                !isCreateProfileSaving && !canSubmitCreateProfile
                  ? "Complete all required fields before adding the invoice profile."
                  : undefined
              }
            >
              {isCreateProfileSaving ? (
                "Saving…"
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {createProfileSubmitLabel}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (createModalOnly) {
    return createProfileModal;
  }

  return <DndProvider backend={HTML5Backend}><div className={embedded ? "" : "p-8"}>
      {embedded ? (
        <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {focusedProfileLabel
                ? `Edit Invoice Profile — ${focusedProfileLabel}`
                : "Edit Invoice Profile"}
            </h3>
            {typeof onCloseEmbedded === "function" ? (
              <button
                type="button"
                onClick={onCloseEmbedded}
                className="app-btn-plain inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-900 dark:text-gray-100"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            ) : null}
          </div>
          {focusProfileIdNormalized != null ? (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Assigned Jackets
                {!isFocusedInvProfileJobNosLoading && !isFocusedInvProfileJobNosError
                  ? ` (${focusedInvProfileJobNos.length})`
                  : ""}
              </h4>
              <ProfileInvJobNosTable
                jobNos={focusedInvProfileJobNos}
                jobs={jacketJobsPayload?.jobs}
                isLoading={isFocusedInvProfileJobNosLoading}
                isError={isFocusedInvProfileJobNosError}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <>
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
          {invoiceProfilePageTitle}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-8 mb-8">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client *
          </label>
          <div
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            aria-readonly="true"
          >
            {clientDisplayName}
          </div>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <button
    className="app-btn-gloss px-6"
    onClick={openCreateProfileModal}
  >
          <Plus className="w-4 h-4" />
          Create Profile
        </button>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label
              htmlFor="billing-profile-status-filter"
              className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
            >
              Status
            </label>
            <select
              id="billing-profile-status-filter"
              value={billingProfileStatusFilter}
              onChange={(e) => setBillingProfileStatusFilter(e.target.value)}
              className="min-w-[10rem] px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              {BILLING_PROFILE_STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="billing-profile-company-filter"
              className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
            >
              Company #
            </label>
            <select
              id="billing-profile-company-filter"
              value={billingProfileCompanyFilter}
              onChange={(e) => setBillingProfileCompanyFilter(e.target.value)}
              className="min-w-[10rem] px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              {billingProfileCompanyFilterOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
        </>
      )}

      {profilesToDisplay.map((profile) => {
    const hasUnsavedService = profileHasUnsavedService(profile);
    return (
      <div key={profile.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm mb-6">
          <div
    className="p-6 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    onClick={() => toggleProfileExpanded(profile.id)}
  >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Client: </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {clients.find((c) => c.code === profile.clientCode)?.name || profile.clientCode || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Invoice Name/Program: </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {profile.invoiceNameProgram || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Company #: </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{profile.companyNumber}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Services: </span>
                  <ProfileServicesCountHover
                    count={countSavedProfileServices(profile.services)}
                    descriptions={serviceInvoiceDescriptionsForHover(
                      savedProfileServices(profile.services)
                    )}
                  />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PO Number: </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {formatProfilePoNumberDisplay(profile.invPoNum)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {profile.activeFlag ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDeactivateProfile(profile.id);
                    }}
                    className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    title="DeActivate Profile"
                  >
                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </button>
                ) : null}
                {profile.isExpanded ? <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
              </div>
            </div>
          </div>

          {profile.isExpanded && <div className="p-6">
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Services</h3>
                    <ProfileServicesCountHover
                      count={countSavedProfileServices(profile.services)}
                      descriptions={serviceInvoiceDescriptionsForHover(
                        savedProfileServices(profile.services)
                      )}
                    />
                  </div>
                  {isInvoiceDescriptionOptionsError ? (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                      Could not load contract services from the server.
                    </p>
                  ) : null}
                  {isInvProfileDetailsFetching &&
                  custIdForQuery != null &&
                  !profile.services.some((s) => s.invProfileDetailId) ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading services…</p>
                  ) : null}
                  {!isInvoiceDescriptionOptionsLoading &&
                  !isInvoiceDescriptionOptionsError &&
                  contractServicesRenderedOptions.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      No contract services for this client. Add services on the Contracts tab first.
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="app-btn-gloss shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => addServiceToProfile(profile.id)}
                  disabled={hasUnsavedService}
                  title={
                    hasUnsavedService
                      ? "Save the current service before adding another"
                      : undefined
                  }
                >
                  <Plus className="w-4 h-4" />
                  Add Invoice Line
                </button>
              </div>

              {countSavedProfileServices(profile.services) > 0 &&
              !(
                isInvProfileDetailsFetching &&
                custIdForQuery != null &&
                !profile.services.some((s) => s.invProfileDetailId)
              ) ? (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    Use the grip handle{" "}
                    <GripVertical className="inline h-4 w-4 align-text-bottom text-blue-600 dark:text-blue-400" />{" "}
                    on each row to drag and reorder how services appear in this list and on the printed
                    invoice. The order will be saved once you release the row.
                  </p>
                </div>
              ) : null}
              {serviceOrderSaveError ? (
                <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                  {serviceOrderSaveError}
                </p>
              ) : null}
              {isSavingServiceOrder ? (
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">Saving service order…</p>
              ) : null}

              {isInvProfileDetailsFetching &&
              custIdForQuery != null &&
              !profile.services.some((s) => s.invProfileDetailId) ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Loading services…
                </div>
              ) : countSavedProfileServices(profile.services) > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-16">
                          <span className="sr-only">Reorder</span>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Services Rendered
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Invoice Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Based On
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Client Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Frequency
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Effective Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                      {savedProfileServices(profile.services).map((service, serviceIndex) => {
                        const showSaveSuccess = !!serviceSaveSuccess[service.id];
                        const { text: invoiceDescText, fullText: invoiceDescFull } =
                          formatInvoiceDescriptionForDisplay(service.invoiceDescription);
                        const serviceExpandKey = `${profile.id}-${service.id}`;
                        const serviceCategory = getCategoryForProfileService(
                          service,
                          contractServiceInvoiceDescriptionOptions
                        );
                        const isLaborService = isLaborCategory(serviceCategory);
                        const isLaborHourLine = isLaborHourService(
                          service,
                          contractServiceInvoiceDescriptionOptions
                        );
                        const serviceOpCodes = isLaborService
                          ? normalizeServiceOpCodes(service)
                          : [];
                        const showBillingClassificationSection =
                          invoiceProfileHasAssignedCompanyNumber(profile) &&
                          !isLaborHourLine &&
                          !isFlatFeeCategory(serviceCategory);
                        const showLaborMappingSection = isLaborHourLine;
                        const hasBillingClassificationData =
                          serviceHasBillingClassificationData(service);
                        const hasLaborMappingData = serviceHasLaborMappingRows(service);
                        const serviceRowExpandable =
                          isLaborService ||
                          showBillingClassificationSection ||
                          showLaborMappingSection ||
                          hasBillingClassificationData ||
                          hasLaborMappingData;
                        const isServiceExpanded =
                          expandedProfileServiceKey === serviceExpandKey;
                        return (
                          <DraggableProfileServiceGroup
                            key={service.id}
                            profileId={profile.id}
                            serviceId={service.id}
                            index={serviceIndex}
                            moveService={moveProfileService}
                            onDragStart={handleServiceDragStart}
                            onDragEnd={handleServiceDragEnd}
                            dragDisabled={isSavingServiceOrder}
                          >
                            {(dragHandleRef, dragDisabled) => (
                              <>
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                              <td className="px-6 py-3 text-sm">
                                <div className="flex items-center gap-1">
                                  <button
                                    ref={dragHandleRef}
                                    type="button"
                                    disabled={dragDisabled}
                                    className="app-btn-plain cursor-grab p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-400 rounded transition-colors"
                                    aria-label="Drag to reorder service"
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </button>
                                {serviceRowExpandable ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedProfileServiceKey(
                                        isServiceExpanded ? null : serviceExpandKey
                                      )
                                    }
                                    className="app-btn-plain p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                    aria-expanded={isServiceExpanded}
                                    aria-label={
                                      isServiceExpanded
                                        ? "Collapse service details"
                                        : "Expand service details"
                                    }
                                  >
                                    {isServiceExpanded ? (
                                      <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    )}
                                  </button>
                                ) : null}
                                </div>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 break-words">
                                {service.servicesRendered || "\u2014"}
                              </td>
                              <td className="px-6 py-3 text-sm">
                                <div
                                  className="text-gray-900 dark:text-gray-100 max-w-[25ch] truncate"
                                  title={invoiceDescFull ?? undefined}
                                >
                                  {invoiceDescText}
                                </div>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 break-words">
                                {service.basedOn || "\u2014"}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 break-words font-medium">
                                {service.clientRate || "\u2014"}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 break-words">
                                {service.frequency || "\u2014"}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-400 break-words">
                                {service.effectiveDate || "\u2014"}
                              </td>
                              <td className="px-6 py-3 text-sm">
                                <ServiceEndDateCell
                                  endDate={service.endDate}
                                  appliedByDisplayName={
                                    serviceHasEndDate(service)
                                      ? loggedInEmployeeDisplayName ||
                                        service.endAppliedByDisplayName
                                      : ""
                                  }
                                />
                              </td>
                              <td className="px-6 py-3 text-sm">
                                <div className="flex items-center gap-2">
                                  {!serviceHasEndDate(service) ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openProfileServiceDrawer(profile.id, service.id)
                                      }
                                      className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                      title="Edit Service"
                                    >
                                      <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    </button>
                                  ) : null}
                                  {!serviceHasEndDate(service) ? (
                                    <button
                                      type="button"
                                      onClick={() => requestRemoveService(profile.id, service.id)}
                                      className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                      title="End service"
                                    >
                                      <Trash2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>

                            {serviceRowExpandable && isServiceExpanded ? (
                              <tr className="bg-gray-50 dark:bg-gray-800">
                                <td colSpan={9} className="px-6 py-4">
                                  <div className="space-y-6">
                                    {showBillingClassificationSection ? (
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                          Billing Classification Data
                                        </h4>
                                        {hasBillingClassificationData ? (
                                          <div className="space-y-4 max-w-3xl">
                                            {ensureBillingClassificationRows(
                                              service,
                                              getInvoiceProfileAssignedCompanyNumbers(profile)
                                            )
                                              .filter((r) =>
                                                String(r?.billingDataClassification ?? "").trim()
                                              )
                                              .map((bcRow, rowIndex, filtered) => {
                                                const classification = String(
                                                  bcRow.billingDataClassification ?? ""
                                                ).trim();
                                                const classifyValue =
                                                  resolveRowBillingDataClassificationAttributeValue(
                                                    bcRow,
                                                    []
                                                  ) ||
                                                  String(
                                                    service.billingDataClassificationValue ?? ""
                                                  ).trim() ||
                                                  "\u2014";
                                                const showCompany =
                                                  getInvoiceProfileAssignedCompanyNumbers(profile)
                                                    .length > 1;
                                                return (
                                                  <dl
                                                    key={bcRow.id ?? rowIndex}
                                                    className={`grid gap-3 sm:grid-cols-2 ${
                                                      rowIndex < filtered.length - 1
                                                        ? "pb-4 border-b border-gray-200 dark:border-gray-700"
                                                        : ""
                                                    }`}
                                                  >
                                                    {showCompany ? (
                                                      <div>
                                                        <dt className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                          Company Number
                                                        </dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                          {bcRow.companyNumber || "\u2014"}
                                                        </dd>
                                                      </div>
                                                    ) : null}
                                                    <div>
                                                      <dt className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                        Billing Data Classification
                                                      </dt>
                                                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                        {classification || "\u2014"}
                                                      </dd>
                                                    </div>
                                                    <div>
                                                      <dt className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                        {classification || "Value"}
                                                      </dt>
                                                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                        {classifyValue}
                                                      </dd>
                                                    </div>
                                                  </dl>
                                                );
                                              })}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                            No billing classification configured for this service.
                                          </p>
                                        )}
                                      </div>
                                    ) : null}
                                    {showLaborMappingSection ? (
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                          Labor Mapping
                                        </h4>
                                        {hasLaborMappingData ? (
                                          <div className="space-y-4 max-w-3xl">
                                            {ensureLaborMappingRows(
                                              service,
                                              getInvoiceProfileAssignedCompanyNumbers(profile)
                                            )
                                              .filter((row) => String(row?.jobNo ?? "").trim())
                                              .map((lmRow, rowIndex, filtered) => {
                                                const showCompany =
                                                  getInvoiceProfileAssignedCompanyNumbers(profile)
                                                    .length > 1;
                                                return (
                                                  <dl
                                                    key={lmRow.id ?? rowIndex}
                                                    className={`grid gap-3 sm:grid-cols-2 ${
                                                      rowIndex < filtered.length - 1
                                                        ? "pb-4 border-b border-gray-200 dark:border-gray-700"
                                                        : ""
                                                    }`}
                                                  >
                                                    {showCompany ? (
                                                      <div>
                                                        <dt className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                          Company Number
                                                        </dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                          {String(lmRow.companyNumber ?? "").trim() ||
                                                            "\u2014"}
                                                        </dd>
                                                      </div>
                                                    ) : null}
                                                    <div>
                                                      <dt className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                        Jackets
                                                      </dt>
                                                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                        {String(lmRow.jobNo ?? "").trim() || "\u2014"}
                                                      </dd>
                                                    </div>
                                                  </dl>
                                                );
                                              })}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                            No labor mapping configured for this service.
                                          </p>
                                        )}
                                      </div>
                                    ) : null}
                                    {isLaborService ? (
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                          Op Codes
                                        </h4>
                                        {serviceOpCodes.length === 0 ? (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                            No op codes assigned
                                          </p>
                                        ) : (
                                          <div className="space-y-2">
                                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                              Op Code
                                            </div>
                                            <ul className="space-y-1.5">
                                              {serviceOpCodes.map((entry) => {
                                                const code = String(entry?.opCode ?? "").trim();
                                                if (!code) {
                                                  return null;
                                                }
                                                return (
                                                  <li
                                                    key={`${code}-${entry.jobNo ?? ""}-${entry.companyNumber ?? ""}`}
                                                    className="text-xs font-mono text-gray-900 dark:text-gray-100"
                                                  >
                                                    {operationLabelByCode.get(code) ?? code}
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ) : null}

                            {showSaveSuccess ? (
                              <tr className="bg-green-50 dark:bg-green-950/40">
                                <td
                                  colSpan={9}
                                  className="px-6 py-2 text-sm text-green-700 dark:text-green-400"
                                  role="status"
                                >
                                  Service has been successfully saved.
                                </td>
                              </tr>
                            ) : null}
                              </>
                            )}
                          </DraggableProfileServiceGroup>
                        );
                      })}
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No services added yet. Click &quot;Add Invoice Line&quot; to create a service.
                </div>
              )}
            </div>}
        </div>
    );
  })}

      {profilesToDisplay.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {custIdForQuery == null
            ? "Open this page from Client Configuration (with custId) to load saved invoice profiles."
            : isProfilesLoading
              ? "Loading invoice profiles…"
              : embedded && focusProfileIdNormalized != null
                ? "Unable to load this invoice profile."
                : embedded
                  ? "No invoice profile selected."
                  : profiles.length > 0 &&
                      (billingProfileStatusFilter !== BILLING_PROFILE_STATUS_FILTER_ALL ||
                        billingProfileCompanyFilter !== BILLING_PROFILE_COMPANY_FILTER_ALL)
                    ? `No invoice profiles match the "${billingProfileActiveFilterDescription(
                        billingProfileStatusFilter,
                        billingProfileCompanyFilter
                      )}" filter.`
                    : 'No profiles created yet. Click "Create Profile" to get started.'}
        </div>}

      {createProfileModal}

      {showProfileServiceDrawer && drawerService && serviceDrawerProfileId != null ? (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={closeProfileServiceDrawer}
            role="presentation"
          />

          <div className="fixed inset-y-0 right-0 max-w-2xl w-full flex">
            <div className="relative w-full bg-white dark:bg-gray-900 shadow-xl flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {drawerService.invProfileDetailId ? "Edit Service" : "Add Service"}
                </h3>
                <button
                  type="button"
                  onClick={closeProfileServiceDrawer}
                  className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {drawerRowErrors._global ? (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {drawerRowErrors._global}
                    </p>
                  ) : null}
                  {isInvoiceDescriptionOptionsLoading ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Loading contract services…
                    </p>
                  ) : null}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Services Rendered *
                    </label>
                    <select
                      value={drawerService.servicesRendered ?? ""}
                      onChange={(e) =>
                        handleServicesRenderedSelect(
                          serviceDrawerProfileId,
                          drawerService.id,
                          e.target.value
                        )
                      }
                      disabled={custIdForQuery == null || isInvoiceDescriptionOptionsLoading}
                      aria-invalid={Boolean(drawerRowErrors.servicesRendered)}
                      className={serviceDrawerFieldClassName(Boolean(drawerRowErrors.servicesRendered))}
                    >
                      <option value="">
                        {isInvoiceDescriptionOptionsLoading
                          ? "Loading…"
                          : "Select services rendered"}
                      </option>
                      {contractServicesRenderedOptions.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                    {drawerRowErrors.servicesRendered ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {drawerRowErrors.servicesRendered}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invoice Description *
                    </label>
                    {drawerInvoiceDescChoices.length > 1 ? (
                      <select
                        value={drawerService.invoiceDescription ?? ""}
                        onChange={(e) =>
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "invoiceDescription",
                            e.target.value
                          )
                        }
                        aria-invalid={Boolean(drawerRowErrors.invoiceDescription)}
                        className={serviceDrawerFieldClassName(
                          Boolean(drawerRowErrors.invoiceDescription)
                        )}
                      >
                        <option value="">Select invoice description</option>
                        {drawerInvoiceDescChoices.map((desc) => (
                          <option key={desc} value={desc}>
                            {desc}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={drawerService.invoiceDescription ?? ""}
                        maxLength={INVOICE_DESCRIPTION_MAX_CHARS}
                        onChange={(e) =>
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "invoiceDescription",
                            e.target.value
                          )
                        }
                        placeholder="Enter invoice description"
                        aria-invalid={Boolean(drawerRowErrors.invoiceDescription)}
                        className={serviceDrawerFieldClassName(
                          Boolean(drawerRowErrors.invoiceDescription)
                        )}
                      />
                    )}
                    {drawerRowErrors.invoiceDescription ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {drawerRowErrors.invoiceDescription}
                      </p>
                    ) : null}
                    {drawerRowErrors._contractServiceLink ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {drawerRowErrors._contractServiceLink}
                      </p>
                    ) : null}
                  </div>

                  {showBillingDataClassificationDropdown ? (
                    <div className="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Billing Data Classification
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Select to indicate how the monthly Billing Report Classify data. Add
                          multiple rows when different company numbers need different
                          classifications.
                        </p>
                      </div>
                      {drawerBillingRows.map((bcRow, rowIndex) => (
                        <BillingClassificationRowEditor
                          key={bcRow.id}
                          row={bcRow}
                          rowIndex={rowIndex}
                          rowCount={drawerBillingRows.length}
                          rowErrors={drawerBillingRowErrors[rowIndex] ?? null}
                          assignedCompanyNumbers={drawerAssignedCompanyNumbers}
                          custIdForQuery={custIdForQuery}
                          fieldClassName={serviceDrawerFieldClassName}
                          onRowPatch={(patch) =>
                            patchServiceBillingClassificationRow(
                              serviceDrawerProfileId,
                              drawerService.id,
                              bcRow.id,
                              patch
                            )
                          }
                          onAddRow={() =>
                            addBillingClassificationRowAfterIndex(
                              serviceDrawerProfileId,
                              drawerService.id,
                              rowIndex
                            )
                          }
                          onRemoveLastRow={() =>
                            removeLastBillingClassificationRow(
                              serviceDrawerProfileId,
                              drawerService.id
                            )
                          }
                          onLookupStateChange={handleBillingRowLookupStateChange}
                          onProductDivisionsChange={handleBillingRowProductDivisionsChange}
                          allRows={drawerBillingRows}
                          productDivisionsByRowId={productDivisionsByRowId}
                          lookupByRowId={billingLookupByRowId}
                        />
                      ))}
                      {typeof drawerRowErrors.billingClassificationRows === "string" ? (
                        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                          {drawerRowErrors.billingClassificationRows}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {showLaborMappingDropdown ? (
                    <div className="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Labor Mapping
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Select the company number and jacket for this labor hour service line. Add
                          multiple rows when different company numbers or jackets are needed.
                        </p>
                      </div>
                      {drawerLaborMappingRows.map((lmRow, rowIndex) => (
                        <LaborMappingRowEditor
                          key={lmRow.id}
                          row={lmRow}
                          rowIndex={rowIndex}
                          rowCount={drawerLaborMappingRows.length}
                          rowErrors={drawerLaborMappingRowErrors[rowIndex] ?? null}
                          assignedCompanyNumbers={drawerAssignedCompanyNumbers}
                          jacketOptions={drawerProfileJobNos}
                          isJacketsLoading={isDrawerProfileJobNosLoading}
                          isJacketsError={isDrawerProfileJobNosError}
                          fieldClassName={serviceDrawerFieldClassName}
                          onRowPatch={(patch) =>
                            patchServiceLaborMappingRow(
                              serviceDrawerProfileId,
                              drawerService.id,
                              lmRow.id,
                              patch
                            )
                          }
                          onAddRow={() =>
                            addLaborMappingRowAfterIndex(
                              serviceDrawerProfileId,
                              drawerService.id,
                              rowIndex
                            )
                          }
                          onRemoveLastRow={() =>
                            removeLastLaborMappingRow(serviceDrawerProfileId, drawerService.id)
                          }
                          allRows={drawerLaborMappingRows}
                        />
                      ))}
                      {typeof drawerRowErrors.laborMappingRows === "string" ? (
                        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                          {drawerRowErrors.laborMappingRows}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Based On
                    </label>
                    <input
                      type="text"
                      value={drawerService.basedOn ?? ""}
                      readOnly
                      aria-readonly="true"
                      placeholder="Set by contract service"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Client Rate
                    </label>
                    <input
                      type="text"
                      value={drawerService.clientRate ?? ""}
                      readOnly
                      aria-readonly="true"
                      aria-invalid={Boolean(drawerRowErrors.clientRate)}
                      placeholder="Set by contract service"
                      className={`w-full px-4 py-2 border rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed ${
                        drawerRowErrors.clientRate
                          ? "border-red-500 dark:border-red-500 ring-1 ring-red-500/40"
                          : "border-gray-300 dark:border-gray-700"
                      }`}
                    />
                    {drawerRowErrors.clientRate ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {drawerRowErrors.clientRate}
                      </p>
                    ) : null}
                  </div>

                  {showPercentageField ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Percentage *
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={drawerService.laborPercent ?? ""}
                        onChange={(e) =>
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "laborPercent",
                            e.target.value
                          )
                        }
                        placeholder="Enter percentage"
                        aria-invalid={Boolean(drawerRowErrors.laborPercent)}
                        className={serviceDrawerFieldClassName(Boolean(drawerRowErrors.laborPercent))}
                      />
                      {drawerRowErrors.laborPercent ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {drawerRowErrors.laborPercent}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {showAddOpCodesDropdown ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Add Op Codes
                      </label>
                      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                        Select one or more op codes (Ctrl+click or Cmd+click for multiple).
                      </p>
                      <select
                        multiple
                        size={opCodesMultiselectSize}
                        value={drawerSelectedOpCodes}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value
                          );
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "opCodes",
                            selected
                          );
                        }}
                        disabled={isOperationsLoading || operationOptions.length === 0}
                        className={serviceDrawerFieldClassName(false)}
                      >
                        {isOperationsLoading ? (
                          <option value="" disabled>
                            Loading…
                          </option>
                        ) : null}
                        {operationOptions.map((opt) => (
                          <option key={opt.opCode} value={opt.opCode}>
                            {opt.descrip ? `${opt.opCode} — ${opt.descrip}` : opt.opCode}
                          </option>
                        ))}
                      </select>
                      {isOperationsError ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          Unable to load op codes.
                        </p>
                      ) : null}
                      {!isOperationsLoading &&
                      !isOperationsError &&
                      operationOptions.length === 0 ? (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          No active op codes are available.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Frequency *
                    </label>
                    <select
                      value={drawerService.frequency ?? ""}
                      onChange={(e) =>
                        updateServiceField(
                          serviceDrawerProfileId,
                          drawerService.id,
                          "frequency",
                          e.target.value
                        )
                      }
                      aria-invalid={Boolean(drawerRowErrors.frequency)}
                      className={serviceDrawerFieldClassName(Boolean(drawerRowErrors.frequency))}
                    >
                      <option value="">Select frequency</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Annually">Annually</option>
                    </select>
                    {drawerRowErrors.frequency ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {drawerRowErrors.frequency}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Effective Date *
                      </label>
                      <input
                        type="date"
                        value={drawerService.effectiveDate ?? ""}
                        onChange={(e) =>
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "effectiveDate",
                            e.target.value
                          )
                        }
                        aria-invalid={Boolean(drawerRowErrors.effectiveDate)}
                        className={serviceDrawerFieldClassName(Boolean(drawerRowErrors.effectiveDate))}
                      />
                      {drawerRowErrors.effectiveDate ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {drawerRowErrors.effectiveDate}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={drawerService.endDate ?? ""}
                        onChange={(e) =>
                          updateServiceField(
                            serviceDrawerProfileId,
                            drawerService.id,
                            "endDate",
                            e.target.value
                          )
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-xs text-blue-900 dark:text-blue-100">
                      Invoice profile services link to contract services for this client. Services and
                      default rates come from the Contracts tab.
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={closeProfileServiceDrawer}
                  className="app-btn-gloss-cancel px-6"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    saveServiceToProfile(serviceDrawerProfileId, drawerService.id)
                  }
                  disabled={
                    isServiceSaving ||
                    (drawerService.invProfileDetailId && !hasProfileServiceFormChanges)
                  }
                  title={
                    drawerService.invProfileDetailId && !hasProfileServiceFormChanges
                      ? "Change a field before updating the service."
                      : undefined
                  }
                  className="app-btn-gloss px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {drawerService.invProfileDetailId ? (
                    <>
                      <Save className="w-4 h-4" />
                      Update Service
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Invoice Line
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeactivateProfileId != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deactivate-profile-dialog-title"
        >
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h2
              id="deactivate-profile-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
            >
              DeActivate Profile?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Please confirm that you want to DeActivate this Profile. Once DeActivated it cannot be
              changed.
            </p>
            {deactivateProfileError ? (
              <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                {deactivateProfileError}
              </p>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDeactivateProfile}
                disabled={isDeactivatingInvProfile}
                className="app-btn-gloss-cancel disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeactivateProfile}
                disabled={isDeactivatingInvProfile}
                className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeactivatingInvProfile ? "Saving…" : "DeActivate Profile"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingRemoveService ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-profile-service-dialog-title"
        >
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h2
              id="end-profile-service-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
            >
              End service?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to end this service? This action cannot be undone.
            </p>
            {endServiceError ? (
              <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                {endServiceError}
              </p>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelRemoveService}
                disabled={isEndingInvProfileDetail}
                className="app-btn-gloss-cancel disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemoveService}
                disabled={isEndingInvProfileDetail}
                className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isEndingInvProfileDetail ? "Saving…" : "Add End Date"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div></DndProvider>;
}
export {
  BillingProfileConfiguration
};
